import { waitForAssistTask } from './assistTaskMonitor';
import { isDirectedTaskAnswer } from './assistVoiceTaskAnswer';
import { hasUnrelatedPlaceSearchTail } from './assistSearchSpeech';
import { parseInAppSearch } from './assistInspectionIntent';
import { appTaskContext } from './assistAppContext';
import { isAssistInspectionRequest, isAssistGoalRequest, isAssistWorkRequest, resolveAssistWorkRequest, isAssistResearchRequest, isAssistDiscoveryRequest } from './assistInspectionIntent';
import { googleEarthOpenPattern, googleOpenPattern, explicitSearchPattern } from './assistWebShortcut';
// Temporary basic Assist mode: keep voice/screen reading, pause action workflows.
const ASSIST_AUTOMATION_ENABLED = import.meta.env.VITE_ASSIST_AUTOMATION_ENABLED === 'true';
const ASSIST_CONTROLLED_TASKS_ENABLED = true;
import { streamNormalChat } from './ariaNormalChatStream';
import { attach as attachAssistAnalyser } from './ariaAudioBus';
import { createVoiceTurn } from './ariaVoiceTurn';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ScreenShareView.css';
import { getApiBase, fetchWithTimeout, ariaUserIdJsonBody } from './apiBase';
// THE SAME ORB, NOT A COPY OF IT (Braeden 9/5). Assist mode had its own
// bespoke circle - five equaliser bars in a button - sitting next to a comment
// that claimed it "matches home screen design". It did not: the real thing is
// AriaOrb, and LAYOUT.md says there is ONE canonical orb body used on every
// surface. A second one that only looks similar is how the two drift apart.
import AriaOrb from './AriaOrb';
import TalkingBall from './TalkingBall';

/**
 * Resolve HowTo-Backend base URL for Assist + TTS.
 * Electron loads the UI from a random 127.0.0.1 port that only serves static files — it does NOT proxy /api.
 * Never fall back to window.location.origin in that case or requests hit the wrong server and fail silently.
 */
function getAssistApiBaseSync() {
  const b = (getApiBase() || '').replace(/\/$/, '');
  if (b) return b;
  if (typeof window !== 'undefined' && window.electronAPI) {
    return 'http://127.0.0.1:3001';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
}

function desktopMicDeniedMessage() {
  const p = typeof window !== 'undefined' && window.electronAPI?.platform;
  if (p === 'win32') {
    return 'Microphone access denied. Allow mic for Aria in Settings → Privacy & Security → Microphone.';
  }
  return 'Microphone access denied. Allow mic for Aria in System Settings → Privacy & Security → Microphone.';
}
const ARIA_AUDIO_PLAYBACK_RATE = 1.0;

function chooseAssistSpeechRate(text) {
  const t = String(text || '').trim();
  if (!t) return ARIA_AUDIO_PLAYBACK_RATE;
  const hasSpellingPattern = /\b[A-Za-z](\s+[A-Za-z]){2,}\b/.test(t);
  if (hasSpellingPattern) return 0.9;
  return ARIA_AUDIO_PLAYBACK_RATE;
}

const CACHE_KEY = 'aria_screen_response_cache';
const CACHE_MAX = 80;
function normalizeQuestion(q) {
  return (typeof q === 'string' ? q.trim().toLowerCase().replace(/\s+/g, ' ') : '').substring(0, 150);
}
const greetingReplies = {
  hello: "Hey.",
  hi: "Hey.",
  hey: "Hey.",
  "hey aria": "Hey.",
  "hello aria": "Hey.",
  "good morning": "Morning.",
  "good afternoon": "Hey.",
  "good evening": "Hey.",
};
function loadResponseCache() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY) : null;
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (_) {
    return {};
  }
}
function saveResponseCache(obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > CACHE_MAX) {
      keys.slice(0, keys.length - CACHE_MAX).forEach((k) => delete obj[k]);
    }
    if (typeof localStorage !== 'undefined') localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch (_) {}
}

// ── Action execution: detect commands like "open google" and actually do them ──
const ACTION_MAP = [
  // Websites
  { pattern: googleEarthOpenPattern, url: 'https://earth.google.com/web/', label: 'Google Earth' },
  { pattern: googleOpenPattern, url: 'https://www.google.com', label: 'Google' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\byoutube\b/i, url: 'https://www.youtube.com', label: 'YouTube' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bgmail\b/i, url: 'https://mail.google.com', label: 'Gmail' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\btwitter\b/i, url: 'https://twitter.com', label: 'Twitter' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\breddit\b/i, url: 'https://www.reddit.com', label: 'Reddit' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bgithub\b/i, url: 'https://github.com', label: 'GitHub' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bamazon\b/i, url: 'https://www.amazon.com', label: 'Amazon' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bnetflix\b/i, url: 'https://www.netflix.com', label: 'Netflix' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bspotify\b/i, url: 'https://open.spotify.com', label: 'Spotify' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\binstagram\b/i, url: 'https://www.instagram.com', label: 'Instagram' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bfacebook\b/i, url: 'https://www.facebook.com', label: 'Facebook' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\btiktok\b/i, url: 'https://www.tiktok.com', label: 'TikTok' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\blinkedin\b/i, url: 'https://www.linkedin.com', label: 'LinkedIn' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\btwitch\b/i, url: 'https://www.twitch.tv', label: 'Twitch' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bdiscord\b/i, url: 'https://discord.com/app', label: 'Discord' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bwhatsapp\b/i, url: 'https://web.whatsapp.com', label: 'WhatsApp' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bnotion\b/i, url: 'https://www.notion.so', label: 'Notion' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bslack\b/i, url: 'https://slack.com', label: 'Slack' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bchatgpt\b/i, url: 'https://chat.openai.com', label: 'ChatGPT' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bclaude\b/i, url: 'https://claude.ai', label: 'Claude' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bstack\s*overflow\b/i, url: 'https://stackoverflow.com', label: 'Stack Overflow' },
  { pattern: /\b(?:open|go to|navigate to|launch|pull up|bring up|load)\b.*\bwikipedia\b/i, url: 'https://www.wikipedia.org', label: 'Wikipedia' },
  // macOS apps (via open command IPC)
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:chrome|google chrome)\b/i, app: 'Google Chrome', label: 'Chrome' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bsafari\b/i, app: 'Safari', label: 'Safari' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:finder|files)\b/i, app: 'Finder', label: 'Finder' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:terminal|command line|cmd)\b/i, app: 'Terminal', label: 'Terminal' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:vscode|vs code|visual studio code)\b/i, app: 'Visual Studio Code', label: 'VS Code' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:messages|imessage)\b/i, app: 'Messages', label: 'Messages' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bmusic\b/i, app: 'Music', label: 'Music' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bnotes\b/i, app: 'Notes', label: 'Notes' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bphotos\b/i, app: 'Photos', label: 'Photos' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:settings|system settings|system preferences|preferences)\b/i, app: 'System Settings', label: 'Settings' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bcalendar\b/i, app: 'Calendar', label: 'Calendar' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bmail\b/i, app: 'Mail', label: 'Mail' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bmaps\b/i, app: 'Maps', label: 'Maps' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bxcode\b/i, app: 'Xcode', label: 'Xcode' },
  // Search action
  { pattern: explicitSearchPattern, searchUrl: 'https://www.google.com/search?q=', label: 'Google Search' },
];

function detectAction(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim();
  for (const entry of ACTION_MAP) {
    const m = t.match(entry.pattern);
    if (m) {
      if (entry.url) return { type: 'url', url: entry.url, label: entry.label };
      if (entry.app) return { type: 'app', app: entry.app, label: entry.label };
      if (entry.searchUrl && m[1]) {
        const query = m[1].trim().replace(/[?.!]+$/, '');
        if (query.length > 0) {
          return { type: 'url', url: entry.searchUrl + encodeURIComponent(query), label: `${entry.label}: ${query}` };
        }
      }
    }
  }
  // Fallback: detect raw URL in command
  const urlMatch = t.match(/\b(?:open|go to|navigate to|visit)\s+(https?:\/\/\S+)/i);
  if (urlMatch) return { type: 'url', url: urlMatch[1], label: urlMatch[1] };
  return null;
}

function executeAction(action) {
  if (!action) return false;
  if (action.type === 'url' && window.electronAPI?.openExternal) {
    console.log('[Assist] Executing action: opening URL', action.url);
    window.electronAPI.openExternal(action.url);
    return true;
  }
  if (action.type === 'app' && window.electronAPI?.openApp) {
    console.log('[Assist] Executing action: opening app', action.app);
    window.electronAPI.openApp(action.app);
    return true;
  }
  // Fallback for app: use open as URL scheme on macOS — won't work, but openExternal can open some apps
  if (action.type === 'app' && window.electronAPI?.openExternal) {
    // For macOS apps, we can't use openExternal, but we have the openApp IPC
    console.log('[Assist] No openApp IPC available, cannot open app:', action.app);
    return false;
  }
  return false;
}

function truncAssist(s, max = 118) {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function ScreenShareView({
  initialStream,
  screenShareError,
  shareRequesting = false,
  onRequestShare,
  onStopShare,
  onBack,
  onPickFiles,
  onTakePicture,
  overlayMuted,
  playbackMuted = false,
  injectQuestion,
  onClearInjectQuestion,
  // Called with every reply this engine produces, so the chat panel the user
  // is actually looking at shows her side of the conversation too. See the
  // relay effect below for why this exists.
  onReply,
  onVisualState,
  conversationHistory = [],
  preferredName = '',
  // ONE MIC, ONE VOICE (Braeden 8/22): "i keep hearing two voices in assist
  // mode, GET RID OF THE OTHER VOICE". This engine ran its own microphone +
  // Whisper loop while the main window's orb kept listening too. Every
  // sentence he said was heard twice, answered twice - once by the
  // conversation pipeline, once by the screen pipeline - and spoken twice.
  // When the host owns the mic, this engine's own listener stays OFF and it
  // only acts on what the host hands it through injectQuestion.
  hostOwnsMic = false,
  voiceTurns: hostVoiceTurns,
}) {
  // Standalone Assist uses the same lifecycle contract as the host mic.
  const localVoiceTurnsRef = useRef(null);
  if (!localVoiceTurnsRef.current) localVoiceTurnsRef.current = createVoiceTurn();
  const voiceTurns = hostVoiceTurns || localVoiceTurnsRef.current;
  const playbackMutedRef = useRef(playbackMuted);
  playbackMutedRef.current = playbackMuted;
  useEffect(() => () => voiceTurns.cancel('assist'), [voiceTurns]);
  const stream = initialStream;
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const replyModeRef = useRef('voice');
  const [answeredByModel, setAnsweredByModel] = useState('');
  const [annotations, setAnnotations] = useState([]);

  // Pre-warm Aria's vision model the moment Assist opens — by the time the
  // user finishes asking, the model is loaded and the answer starts in
  // seconds instead of a cold 30-120s load.
  useEffect(() => {
    try { fetchWithTimeout(`${getApiBase()}/api/screen/warm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', timeout: 4000 }).catch(() => {}); } catch (_) {}
  }, []);
  // Live agent action status — shows what AI is doing (clicking, typing, verifying)
  const [agentStatus, setAgentStatus] = useState(null);
  const agentStatusTimeoutRef = useRef(null);
  const agentStatusFadeRef = useRef(null);
  const [error, setError] = useState('');
  const [micActive, setMicActive] = useState(true);
  const [micSpeaking, setMicSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const [agentDeviceId, setAgentDeviceId] = useState(() => typeof localStorage !== 'undefined' ? localStorage.getItem('shimmer_agent_device_id') || '' : '');
  const [desktopAgentEnabled, setDesktopAgentEnabled] = useState(false);
  const [desktopRunWhenIdleOnly, setDesktopRunWhenIdleOnly] = useState(false);
  const [isDesktopApp, setIsDesktopApp] = useState(false);
  const [screenCaptureGranted, setScreenCaptureGranted] = useState(null); // null = unknown, true = granted, false = denied
  const [ariaSpeaking, setAriaSpeaking] = useState(false);
  const assistDialogueRef = useRef(conversationHistory.slice(-16));
  const assistEpisodeRef = useRef(null);
  const [playbackActive, setPlaybackActive] = useState(false);
  const playingAudioRef = useRef(new Set());
  const visualCallbackRef = useRef(onVisualState);
  visualCallbackRef.current = onVisualState;
  function createAssistAudio(source) {
    const audio = new Audio(source);
    const play = audio.play.bind(audio);
    audio.play = () => playbackMutedRef.current ? Promise.reject(new DOMException('Muted', 'AbortError')) : play();
    if (/^(data:|blob:)/.test(String(source || ''))) attachAssistAnalyser(audio);
    const update = (active) => {
      if (active) playingAudioRef.current.add(audio);
      else playingAudioRef.current.delete(audio);
      setPlaybackActive(playingAudioRef.current.size > 0);
    };
    audio.addEventListener('playing', () => { console.info('[Assist audio] playing', Date.now()); update(true); });
    for (const event of ['waiting', 'pause', 'ended', 'error', 'emptied']) {
      audio.addEventListener(event, () => { console.info('[Assist audio]', event, Date.now(), 'position', audio.currentTime); update(false); });
    }
    return audio;
  }
  useEffect(() => {
    visualCallbackRef.current?.({ speaking: playbackActive, thinking: !!loading && !playbackActive });
  }, [playbackActive, loading]);
  useEffect(() => () => {
    visualCallbackRef.current?.({ speaking: false, thinking: false });
  }, []);

  /** Live speech-to-text partial (interim) — shows “Hearing you…” in the HUD. */
  const [voiceInterim, setVoiceInterim] = useState('');
  /** Command text for this turn (voice or typed) — shown while working / as “last command”. */
  const [activeCommand, setActiveCommand] = useState('');
  const [previewReady, setPreviewReady] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [actionCards, setActionCards] = useState([]);
  const [assistApiBase, setAssistApiBase] = useState(() => getAssistApiBaseSync());

  // ── Auto-chain step loop ──
  // When Aria delivers a step with annotations, we keep narrating the next
  // step automatically as the user acts. This ref tracks the original goal
  // (the user's first question), the chain step counter, and a timer.
  // The chain stops when: (a) no annotations come back, (b) user speaks /
  // types a new question, (c) Aria's reply matches a "task done" pattern,
  // (d) we exceed MAX_CHAIN_TURNS (safety cap).
  const chainGoalRef = useRef('');                  // original user goal (Step 1's question)
  const chainStepRef = useRef(0);                   // 0 = idle, n = how many steps narrated so far
  const chainTimerRef = useRef(null);               // pending setTimeout for next capture
  const chainAbortedRef = useRef(false);            // flipped true when user interrupts
  const MAX_CHAIN_TURNS = 12;                       // safety cap
  const CHAIN_FOLLOWUP_MS = 4500;                   // wait between user-act and re-capture
  // ── Leave Mode ──
  // User-issued state. When true, narration is muted, on-screen overlay is
  // hidden, but background work continues. When the chain finishes, Aria
  // speaks once with the result, then exits Leave.
  const [leaveMode, setLeaveMode] = useState(false);
  const leaveModeRef = useRef(false);
  useEffect(() => { leaveModeRef.current = leaveMode; }, [leaveMode]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const replyAudioRef = useRef(null);
  const ariaSpeakingTimeoutRef = useRef(null);
  const addMenuRef = useRef(null);
  const sendQuestionRef = useRef(null);
  const micPermissionGrantedRef = useRef(false);
  // Screen layout for the "which screen?" question (8/23). Refreshed on mount
  // and after every pick, so the backend always knows the real count.
  const screenInfoRef = useRef(null);
  // What is running on his machine (8/28) — open windows + which one he has
  // been working in. Refreshed on a short interval because he switches windows
  // constantly; a snapshot taken at mount would be stale by the second turn.
  const machineCtxRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const read = async () => {
      try {
        if (window.electronAPI?.workScreen) {
          const info = await window.electronAPI.workScreen();
          if (alive) screenInfoRef.current = info || null;
        }
      } catch (_) {}
      try {
        if (window.electronAPI?.machineContext) {
          const mc = await window.electronAPI.machineContext();
          if (alive) machineCtxRef.current = mc || null;
        }
      } catch (_) {}
    };
    read();
    const id = setInterval(read, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  /** Cancel in-flight /api/screen/analyze when the user speaks again or sends a new command. */
  const assistAbortRef = useRef(null);
  const assistWorkRef = useRef(null);
  useEffect(() => () => {
    const work = assistWorkRef.current;
    assistWorkRef.current = null;
    if (work) fetchWithTimeout(`${work.base}/api/headless/cancel`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId: work.taskId }), timeout: 5000,
    }).catch(() => {});
  }, []);
  /** Only the latest Assist request may clear loading (avoids races after abort). */
  const assistRequestIdRef = useRef(0);

  // ── Ambient Screen Awareness ──
  // Captures background screenshots so the AI always knows what's on screen
  const ambientFramesRef = useRef([]); // Last 3 background captures
  const lastAmbientCaptureRef = useRef(0);
  const screenContextRef = useRef({ activeApp: '', windowTitle: '', browserUrl: '' });
  const recentActionsRef = useRef([]);
  const AMBIENT_INTERVAL = 3000; // 3 seconds between background captures
  const MAX_AMBIENT_FRAMES = 3;

  useEffect(() => {
    streamRef.current = initialStream;
  }, [initialStream]);

  // ── External stop (floating overlay ✕ / OS "stop sharing" bar) ──
  // When the capture stream ends OUTSIDE this view, the exit must still take
  // effect instantly: abort the in-flight analyze, cancel chain follow-ups,
  // and cut any TTS that's mid-sentence. Without this, closing Assist from
  // the pill left Aria talking and a pending reply landing after exit.
  const prevStreamRef = useRef(null);
  useEffect(() => {
    if (!playbackMuted) return;
    console.info('[Assist audio] mute', Date.now(), 'playing', playingAudioRef.current.size);
    voiceTurns.cancel('assist');
    assistAbortRef.current?.abort();
    chainAbortedRef.current = true;
    if (chainTimerRef.current) { clearTimeout(chainTimerRef.current); chainTimerRef.current = null; }
    if (replyAudioRef.current) {
      try { replyAudioRef.current.pause(); } catch (_) {}
      replyAudioRef.current = null;
    }
    for (const audio of playingAudioRef.current) { try { audio.pause(); } catch (_) {} }
    playingAudioRef.current.clear();
    setPlaybackActive(false);
    setAriaSpeaking(false);
    setLoading(false);
  }, [playbackMuted, voiceTurns]);
  useEffect(() => {
    const had = prevStreamRef.current;
    prevStreamRef.current = stream;
    if (!had || stream) return;
    voiceTurns.cancel('assist');
    try { assistAbortRef.current?.abort(); } catch (_) {}
    chainAbortedRef.current = true;
    if (chainTimerRef.current) { clearTimeout(chainTimerRef.current); chainTimerRef.current = null; }
    if (replyAudioRef.current) {
      try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
      replyAudioRef.current = null;
    }
    if (ariaSpeakingTimeoutRef.current) { clearTimeout(ariaSpeakingTimeoutRef.current); ariaSpeakingTimeoutRef.current = null; }
    setLoading(false);
    setAriaSpeaking(false);
    setVoiceInterim('');
    setAnnotations([]);
  }, [stream]);

  // ── Telemetry helper ──
  // Fire-and-forget POST to /api/assist/event so the backend (and the
  // userDetective profile) sees every meaningful Assist-Mode behavior:
  // activation, leave-mode toggle, chain step, annotation delivery, etc.
  // Audit (2026-04-28) found these were all client-only — Aria couldn't
  // learn from them. Now she can.
  const sendAssistEvent = useCallback((event, detail = {}) => {
    try {
      const base = assistApiBase || getAssistApiBaseSync();
      if (!base || !event) return;
      // Use sendBeacon when available (fires even on unmount/page-close),
      // fall back to fetch with keepalive.
      const body = JSON.stringify({ event, detail, ...ariaUserIdJsonBody() });
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${base}/api/assist/event`, blob);
        return;
      }
      fetch(`${base}/api/assist/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* non-fatal */ }
  }, [assistApiBase]);

  // ── Tell the desktop floating overlay we're in Assist Mode while this
  // view is mounted, so the green "ASSIST" badge appears on the pill.
  // The badge is the user's only durable visual cue that Aria is watching.
  // Routed through electronAPI (the main renderer talks to main.js, which
  // forwards to the floating overlay window). No-op in plain browser.
  // Also fires assist_on / assist_off telemetry so the backend learns.
  useEffect(() => {
    const send = (mode) => {
      try {
        if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOverlayAssistMode === 'function') {
          window.electronAPI.sendOverlayAssistMode(mode);
        }
      } catch (_) { /* non-fatal */ }
    };
    send('assist');
    sendAssistEvent('assist_on');
    return () => {
      send(null);
      sendAssistEvent('assist_off');
    };
  }, [sendAssistEvent]);

  // ── Leave Mode telemetry ──
  // Fires when the user toggles leave on/off. Lets userDetective learn
  // which task types make the user say "just do it for me".
  const prevLeaveModeRef = useRef(false);
  useEffect(() => {
    if (leaveMode && !prevLeaveModeRef.current) {
      sendAssistEvent('leave_on', { goal: chainGoalRef.current || '' });
    } else if (!leaveMode && prevLeaveModeRef.current) {
      sendAssistEvent('leave_off');
    }
    prevLeaveModeRef.current = leaveMode;
  }, [leaveMode, sendAssistEvent]);

  // ── On-screen coach overlay — push annotations to the transparent
  // click-through fullscreen window so Aria's circles/highlights appear
  // ON the user's actual app, not just inside the in-panel preview.
  // Fires every time `annotations` or `reply` changes; clears on unmount.
  // Also fires annotation_delivered telemetry so userDetective learns
  // which kinds of UI elements Aria points at most often.
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.electronAPI || typeof window.electronAPI.sendOnScreenAnnotations !== 'function') return;
      window.electronAPI.sendOnScreenAnnotations({
        annotations: Array.isArray(annotations) ? annotations : [],
        message: typeof reply === 'string' ? reply : '',
      });
    } catch (_) { /* non-fatal */ }
    if (Array.isArray(annotations) && annotations.length > 0) {
      const types = annotations.map((a) => a && a.type).filter(Boolean);
      sendAssistEvent('annotation_delivered', {
        count: annotations.length,
        types,
        chainStep: chainStepRef.current || 0,
      });
    }
  }, [annotations, reply, sendAssistEvent]);

  // ── THINKING state on the floating mic ──
  // Show amber soft-ripple while a request is in flight AND Aria isn't
  // already speaking. This fills the dead-air gap between "I asked her" and
  // "she answered" — without it, the mic just sits flat-grey and the user
  // assumes the app froze. Cleared when speech starts or when loading ends.
  useEffect(() => {
    const thinking = !!loading && !ariaSpeaking && !leaveMode;
    try {
      if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOverlayThinking === 'function') {
        window.electronAPI.sendOverlayThinking(thinking);
      }
    } catch (_) { /* non-fatal */ }
  }, [loading, ariaSpeaking, leaveMode]);

  useEffect(() => () => {
    // Final clear on unmount so the overlay closes itself out.
    try {
      if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOnScreenAnnotations === 'function') {
        window.electronAPI.sendOnScreenAnnotations({ annotations: [], message: '' });
      }
    } catch (_) {}
    // Belt-and-braces: nothing may survive the view — kill the in-flight
    // analyze, the chain timer, and any TTS still playing.
    try { assistAbortRef.current?.abort(); } catch (_) {}
    chainAbortedRef.current = true;
    if (chainTimerRef.current) { clearTimeout(chainTimerRef.current); chainTimerRef.current = null; }
    if (replyAudioRef.current) {
      try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
      replyAudioRef.current = null;
    }
    if (ariaSpeakingTimeoutRef.current) { clearTimeout(ariaSpeakingTimeoutRef.current); ariaSpeakingTimeoutRef.current = null; }
  }, []);

  useEffect(() => {
    setAssistApiBase(getAssistApiBaseSync());
    if (typeof window !== 'undefined' && window.electronAPI?.getBackendApiBase) {
      window.electronAPI
        .getBackendApiBase()
        .then((u) => {
          if (u && String(u).trim()) setAssistApiBase(String(u).trim().replace(/\/$/, ''));
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.electronAPI?.getAgentConfig) {
      setIsDesktopApp(true);
      window.electronAPI.getAgentConfig().then((config) => {
        if (config?.deviceId) setAgentDeviceId(config.deviceId);
        setDesktopAgentEnabled(!!config?.agentEnabled);
        setDesktopRunWhenIdleOnly(!!config?.agentRunWhenIdleOnly);
      });
      window.electronAPI.getScreenCaptureStatus?.().then((status) => {
        const s = String(status || '').toLowerCase();
        if (s === 'denied') setScreenCaptureGranted(false);
        else if (['granted', 'authorized'].includes(s)) setScreenCaptureGranted(true);
        else setScreenCaptureGranted(null); // unknown / not-determined — don't show "turn on" UI if OS status is ambiguous
      }).catch(() => setScreenCaptureGranted(null));
    }
  }, []);

  // ── Agent Status Listener ──
  // Listen for live agent action status from desktop (clicking, typing, verifying)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.onAgentStatus) return;
    const handler = (status) => {
      // Clear any pending auto-hide timer
      if (agentStatusTimeoutRef.current) clearTimeout(agentStatusTimeoutRef.current);
      if (agentStatusFadeRef.current) clearTimeout(agentStatusFadeRef.current);

      setAgentStatus(status);

      // Auto-hide after completion phases
      if (status?.phase === 'done' || status?.phase === 'verified' || status?.phase === 'error') {
        agentStatusTimeoutRef.current = setTimeout(() => {
          setAgentStatus((prev) => prev ? { ...prev, fading: true } : null);
          agentStatusFadeRef.current = setTimeout(() => setAgentStatus(null), 500);
        }, 3000);
      }
    };
    window.electronAPI.onAgentStatus(handler);
    return () => {
      if (agentStatusTimeoutRef.current) clearTimeout(agentStatusTimeoutRef.current);
      if (agentStatusFadeRef.current) clearTimeout(agentStatusFadeRef.current);
    };
  }, []);

  // ── Speak Listener ──
  // When the agent finishes deep work or needs to tell the user something, play TTS.
  // Both the IPC subscription AND the audio element listeners are tracked so
  // the cleanup function tears them down on unmount / deps change. Without
  // this, every assistApiBase change stacks another speak handler, and old
  // audio elements stay alive (closure of their event listeners holds them).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.onSpeak) return;
    let cancelled = false;
    const audioListenersToRemove = []; // [{el, type, fn}]

    const handler = async (data) => {
      const text = data?.text;
      if (!text || cancelled || playbackMutedRef.current) return;
      const existing = voiceTurns.current('assist');
      const voiceTurn = existing || voiceTurns.begin('assist', { timeoutMs: 30000 });
      const finishWork = existing ? voiceTurn.retain() : () => voiceTurn.finish();
      setReply(text);
      try {
        const base = assistApiBase || getAssistApiBaseSync();
        const ttsRes = await fetch(`${base}/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, ...ariaUserIdJsonBody() }),
        });
        if (cancelled || !ttsRes.ok) return;
        const buf = await ttsRes.arrayBuffer();
        if (cancelled || !voiceTurn.isCurrent()) return;
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
        if (audioData.length <= 100) return;
        if (replyAudioRef.current) {
          try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
        }
        const audio = createAssistAudio(audioData);
        replyAudioRef.current = audio;
        const onPlay = () => setAriaSpeaking(true);
        const onEnded = () => { if (replyAudioRef.current === audio) replyAudioRef.current = null; setAriaSpeaking(false); };
        const onError = () => { if (replyAudioRef.current === audio) replyAudioRef.current = null; setAriaSpeaking(false); };
        audio.addEventListener('play', onPlay);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError);
        audioListenersToRemove.push({ el: audio, type: 'play', fn: onPlay });
        audioListenersToRemove.push({ el: audio, type: 'ended', fn: onEnded });
        audioListenersToRemove.push({ el: audio, type: 'error', fn: onError });
        voiceTurn.play(audio).catch(() => setAriaSpeaking(false));
      } catch (_) {} finally { finishWork(); }
    };
    const off = window.electronAPI.onSpeak(handler);
    return () => {
      cancelled = true;
      try { off && off(); } catch (_) {}
      for (const { el, type, fn } of audioListenersToRemove) {
        try { el.removeEventListener(type, fn); } catch (_) {}
      }
    };
  }, [assistApiBase, voiceTurns]);

  // When user returns to the app after enabling Screen Recording, re-check and auto-start share once.
  // hasAutoRetriedRef prevents a loop: on macOS 26 Tahoe, getScreenCaptureStatus alternates between
  // 'granted' and 'not-determined' on consecutive calls, so prevGrantedRef can flip repeatedly and
  // trigger onRequestShare on every focus event.  We only ever auto-retry once per component mount.
  const prevGrantedRef = useRef(null);
  const hasAutoRetriedRef = useRef(false);
  useEffect(() => {
    if (!isDesktopApp || stream || !onRequestShare) return;
    const onVisible = () => {
      window.electronAPI?.getScreenCaptureStatus?.().then((status) => {
        const s = String(status || '').toLowerCase();
        const granted = ['granted', 'authorized'].includes(s);
        // Fire auto-start if the status just became granted AND previously was either
        // explicitly not-granted (false) OR was unknown/not-determined (null).
        // Also guard with hasAutoRetriedRef so we only do this once total — macOS 26
        // status flipping would otherwise re-trigger on every window focus.
        if (granted && prevGrantedRef.current !== true && !hasAutoRetriedRef.current) {
          hasAutoRetriedRef.current = true;
          setScreenCaptureGranted(true);
          onRequestShare();
        }
        prevGrantedRef.current = granted;
      }).catch(() => {});
    };
    const handleVisibility = () => { if (document.visibilityState === 'visible') onVisible(); };
    const handleFocus = () => { onVisible(); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isDesktopApp, stream, onRequestShare]);

  useEffect(() => {
    prevGrantedRef.current = screenCaptureGranted;
  }, [screenCaptureGranted]);

  // Auto-request screen share on mount in Electron — one-click Assist experience.
  // If the user navigated here (any route) and there's no stream, start capture immediately.
  const autoRequestedRef = useRef(false);
  useEffect(() => {
    if (!isDesktopApp || stream || !onRequestShare || shareRequesting || autoRequestedRef.current) return;
    autoRequestedRef.current = true;
    // Small delay so the component renders "Starting Assist…" before the capture call
    const timer = setTimeout(() => { onRequestShare(); }, 100);
    return () => clearTimeout(timer);
  }, [isDesktopApp, stream, onRequestShare, shareRequesting]);

  useEffect(() => {
    if (!stream) {
      setPreviewReady(false);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    setPreviewReady(false);
    video.srcObject = stream;
    const tryPlay = () => {
      video.play().then(() => setPreviewReady(true)).catch(() => {});
    };
    const onReady = () => {
      tryPlay();
    };
    if (video.readyState >= 2) {
      onReady();
    } else {
      video.onloadedmetadata = onReady;
      video.onloadeddata = onReady;
    }
    video.onplaying = () => setPreviewReady(true);
    video.onstalled = tryPlay;
    video.onsuspend = tryPlay;
    return () => {
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.onplaying = null;
      video.onstalled = null;
      video.onsuspend = null;
    };
  }, [stream]);

  useEffect(() => {
    if (!addMenuOpen) return;
    const close = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setAddMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [addMenuOpen]);

  // ── Ambient Screen Awareness: periodic background captures ──
  useEffect(() => {
    if (!videoRef.current?.srcObject) return;

    const interval = setInterval(() => {
      try {
        const video = videoRef.current;
        if (!video || video.videoWidth === 0) return;

        const canvas = document.createElement('canvas');
        // Lower resolution for ambient captures to save memory
        const scale = Math.min(1, 640 / video.videoWidth);
        canvas.width = video.videoWidth * scale;
        canvas.height = video.videoHeight * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const frame = {
          base64: canvas.toDataURL('image/jpeg', 0.5).split(',')[1],
          timestamp: Date.now(),
          width: canvas.width,
          height: canvas.height,
        };

        ambientFramesRef.current.push(frame);
        if (ambientFramesRef.current.length > MAX_AMBIENT_FRAMES) {
          ambientFramesRef.current.shift();
        }
        lastAmbientCaptureRef.current = Date.now();
      } catch (e) {
        // Silent fail for background captures
      }
    }, AMBIENT_INTERVAL);

    return () => clearInterval(interval);
  }, [videoRef.current?.srcObject]);

  // ── Desktop Context Collection ──
  // Pulls active app, window title, browser URL from desktop app
  useEffect(() => {
    if (!window.electronAPI) return;

    const collectContext = async () => {
      try {
        if (window.electronAPI.getActiveWindowContext) {
          const ctx = await window.electronAPI.getActiveWindowContext();
          screenContextRef.current = ctx || {};
        }
      } catch (e) { /* silent */ }

      try {
        if (window.electronAPI.getRecentActions) {
          const actions = await window.electronAPI.getRecentActions();
          recentActionsRef.current = actions || [];
        }
      } catch (e) { /* silent */ }
    };

    collectContext(); // Initial collection
    const interval = setInterval(collectContext, 2000); // Every 2 seconds
    return () => clearInterval(interval);
  }, []);

  // ── Track user interactions with Aria for context ──
  useEffect(() => {
    const handleInteraction = (e) => {
      if (!window.electronAPI?.logUserAction) return;
      const target = e.target;
      const detail = target.tagName + (target.className ? '.' + (typeof target.className === 'string' ? target.className.split(' ')[0] : '') : '') + (target.textContent ? ': ' + target.textContent.slice(0, 50) : '');
      window.electronAPI.logUserAction({
        type: 'click',
        detail,
        timestamp: Date.now(),
      });
    };

    document.addEventListener('click', handleInteraction, { passive: true, capture: true });
    return () => document.removeEventListener('click', handleInteraction, { capture: true });
  }, []);

  // ── Enhanced Visual Guide Annotations ──
  useEffect(() => {
    if (!annotations.length || !canvasRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animFrame;
    let pulsePhase = 0;

    const COLORS = {
      click: { stroke: '#22c55e', fill: 'rgba(34, 197, 94, 0.18)', label: '#22c55e', bg: 'rgba(0,0,0,0.85)' },       // Green — click targets
      info: { stroke: '#60a5fa', fill: 'rgba(96, 165, 250, 0.12)', label: '#60a5fa', bg: 'rgba(0,0,0,0.85)' },        // Blue — informational
      caution: { stroke: '#f59e0b', fill: 'rgba(245, 158, 11, 0.15)', label: '#f59e0b', bg: 'rgba(0,0,0,0.85)' },     // Orange — caution/warning
      primary: { stroke: 'var(--accent)', fill: 'rgba(252, 211, 77, 0.15)', label: 'var(--accent)', bg: 'rgba(0,0,0,0.85)' },    // Green — default/primary
      highlight: { stroke: '#facc15', fill: 'rgba(250, 204, 21, 0.28)', label: '#fde047', bg: 'rgba(0,0,0,0.85)' },   // Yellow — text highlighter
    };

    const getColor = (a, idx) => {
      if (a.color && COLORS[a.color]) return COLORS[a.color];
      // Auto-assign by type
      if (a.type === 'circle' || a.type === 'arrow') return idx === 0 ? COLORS.click : COLORS.info;
      if (a.type === 'highlight') return COLORS.highlight; // Yellow highlighter for text
      if (a.type === 'rect') return COLORS.info;
      return COLORS.primary;
    };

    const drawRoundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    const drawLabel = (text, x, y, colors, stepNum) => {
      const label = stepNum != null ? `${stepNum}. ${text}` : text;
      const fontSize = Math.max(14, Math.min(20, canvas.width / 60));
      ctx.font = `600 ${fontSize}px "DM Sans", -apple-system, sans-serif`;
      const metrics = ctx.measureText(label);
      const padX = 10;
      const padY = 6;
      const pillW = metrics.width + padX * 2;
      const pillH = fontSize + padY * 2;
      const pillX = x - pillW / 2;
      const pillY = y - pillH - 4;

      // Shadow
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;

      // Pill background
      drawRoundedRect(pillX, pillY, pillW, pillH, pillH / 2);
      ctx.fillStyle = colors.bg;
      ctx.fill();

      // Pill border
      ctx.strokeStyle = colors.stroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Label text
      ctx.fillStyle = colors.label;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x, pillY + pillH / 2);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';

      return { pillX, pillY, pillW, pillH };
    };

    const drawArrowHead = (fromX, fromY, toX, toY, size) => {
      const angle = Math.atan2(toY - fromY, toX - fromX);
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - size * Math.cos(angle - Math.PI / 6), toY - size * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(toX - size * Math.cos(angle + Math.PI / 6), toY - size * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    };

    const draw = () => {
      if (video.videoWidth === 0) { animFrame = requestAnimationFrame(draw); return; }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      // ── Dim overlay with cutouts for annotated areas ──
      // Only circles and rects get spotlight cutouts; highlights are yellow washes drawn on top
      if (annotations.some(a => a.type === 'circle' || a.type === 'rect')) {
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-out';
        annotations.forEach((a) => {
          if (a.type === 'circle' && a.r != null) {
            const cx = a.x * w, cy = a.y * h;
            const r = (a.r * Math.min(w, h)) / 2;
            const cutR = r + 24; // Generous cutout so the pulse rings are visible
            ctx.beginPath();
            ctx.arc(cx, cy, cutR, 0, Math.PI * 2);
            ctx.fill();
          } else if (a.type === 'rect') {
            const rx = (a.x || 0) * w - 12;
            const ry = (a.y || 0) * h - 12;
            const rw = (a.w || 0.1) * w + 24;
            const rh = (a.h || 0.08) * h + 24;
            drawRoundedRect(rx, ry, rw, rh, 8);
            ctx.fill();
          } else if (a.type === 'highlight') {
            // Cut out highlight areas too so yellow wash is visible against the dim
            const rx = (a.x || 0) * w - 6;
            const ry = (a.y || 0) * h - 6;
            const rw = (a.w || 0.1) * w + 12;
            const rh = (a.h || 0.04) * h + 12;
            ctx.fillRect(rx, ry, rw, rh);
          }
        });
        ctx.restore();
      }

      // ── Draw each annotation ──
      pulsePhase += 0.04;
      const pulse = 0.6 + 0.4 * Math.sin(pulsePhase);

      annotations.forEach((a, idx) => {
        const colors = getColor(a, idx);
        const stepNum = annotations.length > 1 ? idx + 1 : null;

        if (a.type === 'circle' && a.r != null) {
          const cx = a.x * w, cy = a.y * h;
          const r = (a.r * Math.min(w, h)) / 2;
          const isPrimary = idx === 0;

          // Glow effect — all circles glow, primary glows stronger
          ctx.shadowColor = colors.stroke;
          ctx.shadowBlur = isPrimary ? 18 * pulse : 8;

          // Filled background
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fillStyle = colors.fill;
          ctx.fill();

          // Main stroke — thick, clear ring
          ctx.strokeStyle = colors.stroke;
          ctx.lineWidth = isPrimary ? 4 : 3;
          ctx.setLineDash([]);
          ctx.stroke();

          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;

          // Outer pulse ring — ALL circles pulse so the user sees them animate
          ctx.beginPath();
          const ringRadius = isPrimary ? r + 8 * pulse : r + 5 * pulse;
          ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
          ctx.strokeStyle = colors.stroke;
          ctx.lineWidth = isPrimary ? 2.5 : 1.5;
          ctx.globalAlpha = isPrimary ? (1 - pulse * 0.4) : (0.7 - pulse * 0.3);
          ctx.stroke();
          ctx.globalAlpha = 1;

          // Second outer ring for primary — double pulse for extra visibility
          if (isPrimary) {
            ctx.beginPath();
            ctx.arc(cx, cy, r + 16 * pulse, 0, Math.PI * 2);
            ctx.strokeStyle = colors.stroke;
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.4 - pulse * 0.3;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }

          // Label
          if (a.label) drawLabel(a.label, cx, cy - r - 12, colors, stepNum);

        } else if (a.type === 'highlight') {
          // ── Text Highlighter — yellow wash like a real highlighter pen ──
          const rx = (a.x || 0) * w;
          const ry = (a.y || 0) * h;
          const rw = (a.w || 0.1) * w;
          const rh = (a.h || 0.04) * h;

          // Yellow highlighter fill — warm, semi-transparent like marker on paper
          ctx.fillStyle = 'rgba(250, 204, 21, 0.35)';
          // Slightly slanted/organic shape to feel like a real highlight stroke
          ctx.beginPath();
          const skew = rh * 0.08;
          ctx.moveTo(rx - 2, ry + skew);
          ctx.lineTo(rx + rw + 2, ry - skew);
          ctx.lineTo(rx + rw + 2, ry + rh + skew);
          ctx.lineTo(rx - 2, ry + rh - skew);
          ctx.closePath();
          ctx.fill();

          // Subtle bottom border like ink bleed
          ctx.strokeStyle = 'rgba(250, 204, 21, 0.5)';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(rx, ry + rh);
          ctx.lineTo(rx + rw, ry + rh);
          ctx.stroke();

          // Label above
          if (a.label) drawLabel(a.label, rx + rw / 2, ry - 4, COLORS.highlight, stepNum);

        } else if (a.type === 'rect') {
          const rx = (a.x || 0) * w;
          const ry = (a.y || 0) * h;
          const rw = (a.w || 0.1) * w;
          const rh = (a.h || 0.08) * h;

          // Filled background
          drawRoundedRect(rx, ry, rw, rh, 6);
          ctx.fillStyle = colors.fill;
          ctx.fill();

          // Border
          ctx.strokeStyle = colors.stroke;
          ctx.lineWidth = 2.5;
          ctx.setLineDash([]);
          ctx.stroke();

          // Label
          if (a.label) drawLabel(a.label, rx + rw / 2, ry - 4, colors, stepNum);

        } else if (a.type === 'arrow') {
          // Arrow from (x,y) pointing to (toX, toY) with label
          const fromX = (a.x || 0) * w, fromY = (a.y || 0) * h;
          const toX = (a.toX || a.x || 0) * w, toY = (a.toY || a.y || 0) * h;

          // Arrow line
          ctx.strokeStyle = colors.stroke;
          ctx.lineWidth = 2.5;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(fromX, fromY);
          ctx.lineTo(toX, toY);
          ctx.stroke();

          // Arrowhead
          ctx.fillStyle = colors.stroke;
          drawArrowHead(fromX, fromY, toX, toY, 14);

          // Target dot
          ctx.beginPath();
          ctx.arc(toX, toY, 6, 0, Math.PI * 2);
          ctx.fillStyle = colors.stroke;
          ctx.fill();

          // Label at the from end
          if (a.label) drawLabel(a.label, fromX, fromY - 6, colors, stepNum);
        }
      });

      // Keep animating if there are annotations (for pulse effect)
      animFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (animFrame) cancelAnimationFrame(animFrame); };
  }, [annotations]);

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !stream) return null;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;
    try {
      const maxW = 1280;
      const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
      const w = Math.round(video.videoWidth * scale);
      const h = Math.round(video.videoHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
      const parts = dataUrl.split(',');
      return parts[1] || null;
    } catch (_) {
      return null;
    }
  }, [stream]);

  /** Screen tracks often report 0×0 for a few hundred ms after getDisplayMedia — wait before analyze. */
  const waitForCaptureBase64 = useCallback(async (maxMs = 5000) => {
    const tryCapture = () => {
      try {
        return captureFrame();
      } catch (_) {
        return null;
      }
    };
    let b = tryCapture();
    if (b) return b;
    const video = videoRef.current;
    if (video && typeof video.requestVideoFrameCallback === 'function') {
      await new Promise((resolve) => {
        const done = () => resolve();
        const ms = setTimeout(done, 200);
        try {
          video.requestVideoFrameCallback(() => {
            clearTimeout(ms);
            done();
          });
        } catch (_) {
          clearTimeout(ms);
          done();
        }
      });
      b = tryCapture();
      if (b) return b;
    }
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 100));
      b = tryCapture();
      if (b) return b;
    }
    return null;
  }, [captureFrame]);

  const responseCacheRef = useRef(null);
  if (!responseCacheRef.current) responseCacheRef.current = loadResponseCache();

  // ── Aria speaks errors aloud so the user always knows what's wrong ──
  const speakError = useCallback(async (message, parentTurn) => {
    if (playbackMutedRef.current) return;
    const voiceTurn = parentTurn || voiceTurns.begin('assist', { timeoutMs: 30000 });
    if (!voiceTurn.isCurrent()) return;
    const finishWork = parentTurn ? voiceTurn.retain() : () => voiceTurn.finish();
    setReply(message);
    setError(message);
    try {
      const base = assistApiBase || getAssistApiBaseSync();
      const ttsRes = await fetchWithTimeout(`${base}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: message, ...ariaUserIdJsonBody() }),
        timeout: 8000,
      });
      if (ttsRes.ok) {
        const buf = await ttsRes.arrayBuffer();
        if (!voiceTurn.isCurrent()) return;
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
        if (audioData.length > 100) {
          if (replyAudioRef.current) {
            try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
          }
          const audio = createAssistAudio(audioData);
          replyAudioRef.current = audio;
          // Use single-handler properties (audio.onX = …) instead of
          // addEventListener so the browser cleans these up when the
          // Audio element is GC'd. addEventListener without a matching
          // remove leaks the audio + closures forever.
          audio.onplay = () => setAriaSpeaking(true);
          audio.onended = () => { if (replyAudioRef.current === audio) replyAudioRef.current = null; setAriaSpeaking(false); };
          audio.onerror = () => { if (replyAudioRef.current === audio) replyAudioRef.current = null; setAriaSpeaking(false); };
          voiceTurn.play(audio).catch(() => setAriaSpeaking(false));
        }
      }
    } catch (_) {
      // TTS failed — at least the visual error is shown.
    } finally { finishWork(); }
  }, [assistApiBase, voiceTurns]);

  const sendQuestion = useCallback(async (overrideQuestion, opts = {}) => {
    if (overrideQuestion == null) opts = { ...opts, inputMode: 'text' };
    if (playbackMutedRef.current && opts.inputMode !== 'text') { opts.voiceTurn?.finish(); return; }
    const q = appTaskContext.resolve((overrideQuestion != null ? overrideQuestion : question).trim(), {voice:opts.inputMode==='voice',history:assistDialogueRef.current});
    if (!q) { opts.voiceTurn?.finish(); return; }
    replyModeRef.current = opts.inputMode === 'text' ? 'text' : 'voice';
    const inspectionOnly = isAssistInspectionRequest(q) || isAssistGoalRequest(q);
    const discoveryRequest = isAssistDiscoveryRequest(q);
    const researchRequest = isAssistResearchRequest(q);
    const resolvedWorkRequest = resolveAssistWorkRequest(q, assistDialogueRef.current);
    const workRequest = discoveryRequest || !!resolvedWorkRequest || (ASSIST_AUTOMATION_ENABLED && researchRequest);
    fetch(`${assistApiBase || getAssistApiBaseSync()}/api/pipeline/event`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: 'assist.request.routed', text: q.slice(0, 500), meta: {
        inputMode: opts.inputMode, isDesktopApp, workRequest: !!workRequest,
        inspectionOnly, hasActiveTask: !!assistWorkRef.current,
      } }),
    }).catch(() => {});
    const screenReference = inspectionOnly || /\b(screen|monitor|display|screenshot|window|on my desktop)\b/i.test(q)
      || /\b(?:what (?:is|am i|are we)|can you (?:see|read)|look at|read|describe)\b[^.!?]{0,45}\b(?:this|that|here|looking at|seeing)\b/i.test(q);
    opts = { ...opts, requireScreen: !!opts.requireScreen || screenReference };
    const voiceTurn = opts.voiceTurn || voiceTurns.begin('assist', { timeoutMs: 240000 });
    if (!voiceTurn.isCurrent()) return;
    const placeSearch = parseInAppSearch(q);
    if (placeSearch?.app === 'Google Earth' && hasUnrelatedPlaceSearchTail(placeSearch.query)) {
      await speakError('I heard additional speech mixed with the place search. Please repeat just the location and the actions you want in Google Earth.', voiceTurn);
      voiceTurn.finish();
      return;
    }
    // Follow the authenticated task directly; never send its answers back through
    // keyword-based conversation middleware or claim that merely queuing is done.
    if (ASSIST_CONTROLLED_TASKS_ENABLED && isDesktopApp && (assistWorkRef.current || workRequest)) {
      let taskHandled = true;
      const base = assistApiBase || getAssistApiBaseSync();
      const post = async (path, body) => {
        const res = await fetchWithTimeout(`${base}/api/headless/${path}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body), timeout: 15000,
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || 'Task request failed');
        return data;
      };
      
      const monitorTask = async (taskId) => {
        const progress = await waitForAssistTask({
          isCurrent: () => assistWorkRef.current?.taskId === taskId,
          readStatus: async () => {
            const response = await fetchWithTimeout(base + '/api/headless/status?taskId=' + encodeURIComponent(taskId), { timeout: 5000 });
            if (!response.ok) throw new Error('Unable to check the current task');
            return response.json();
          },
        });
        if (!progress) return;
        if (progress.status !== 'waiting_user') assistWorkRef.current = null;
        const message = progress.status === 'waiting_user'
          ? progress.pendingQuestion || 'What should I know before continuing?'
          : progress.replyText || progress.verdict || progress.summary || 'The task ended without a verified result.';
        const links = (progress.sources || []).filter(source => /^https?:\/\//i.test(source.url || '')).map(source => source.title + '\n' + source.url).join('\n\n');
        // Do not interrupt a newer voice turn or bypass mute. A task may outlive
        // its original speech lease; its result must still reach the chat.
        if (voiceTurn.isCurrent()) await speakError(message, voiceTurn);
        else if (!voiceTurns.current() && !playbackMutedRef.current) await speakError(message);
        setReply([message, links].filter(Boolean).join('\n\n'));
        setError('');
      };
try {
        let work = assistWorkRef.current;
        let status = null;
        if (work) {
          const res = await fetchWithTimeout(`${base}/api/headless/status?taskId=${encodeURIComponent(work.taskId)}`, { timeout: 5000 });
          if (res.status === 404) { assistWorkRef.current = null; work = null; }
          else if (!res.ok) throw new Error('Unable to check the current task');
          else status = await res.json();
          if (status && ['complete', 'failed', 'cancelled'].includes(status.status)) {
            assistWorkRef.current = null; work = null;
            if (/^(?:is it done|did (?:it|you) (?:work|finish)|what(?:'s| is) the status)[?.!]*$/i.test(q)) {
              const message = status.verdict || status.summary || 'The task ended without a verified result.';
              setReply(message); await speakError(message, voiceTurn); setError('');
              return;
            }
          }
        }
        if (work && /^(?:please\s+)?(?:stop|cancel|pause)(?:\s+(?:it|that|the (?:test )?task|working))?[.!]*$/i.test(q)) {
          await post('cancel', { taskId: work.taskId });
          assistWorkRef.current = null;
          setReply('Stopped.');
          await speakError('Stopped.', voiceTurn); setError('');
          return;
        }
        if (work && status?.status === 'waiting_user') {
          if (opts.inputMode === 'voice' && !isDirectedTaskAnswer(q, status.pendingQuestion)) {
            // Leave the pending task and screen target intact. No synthetic
            // answer, repeated spoken question, or replacement task is emitted.
            fetch(`${base}/api/pipeline/event`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ stage: 'assist.answer.ignored', meta: { reason: 'undirected_voice', taskId: work.taskId } }) }).catch(() => {});
            return;
          }
          await post('answer', { taskId: work.taskId, answer: q });
          setReply('Continuing with your answer.');
          await monitorTask(work.taskId);
          return;
        }
        if (work) {
          const message = status?.statusMessage || 'The current task is still running.';
          setReply(message); await speakError(message, voiceTurn); setError('');
          return;
        }
        if (workRequest) {
          const cfg = await window.electronAPI?.getAgentConfig?.();
          if (!cfg?.deviceId) throw new Error('Desktop control is not connected');
          if (!voiceTurn.isCurrent()) return;
          const registration = await fetchWithTimeout(`${base}/api/relay/register-device`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId: cfg.deviceId, kind: window.electronAPI.platform === 'darwin' ? 'mac' : 'pc', capabilities: ['assist'] }),
            timeout: 5000,
          });
          if (!registration.ok) throw new Error('HEADLESS_DEVICE_FORBIDDEN');
          if (!voiceTurn.isCurrent()) return;
          // Use the same app/monitor selection as inspection before the planner
          // captures its first state. Otherwise it starts on the chat monitor.
          const target = await window.electronAPI?.selectTaskScreen?.(resolvedWorkRequest || q);
          if (target?.matched && target.app) {
            const focused = await window.electronAPI?.executeCommand?.({ type: 'focus_window', title: target.title || target.app });
            if (!focused?.ok) throw new Error('Could not bring the task app into view');
          }
          if (!voiceTurn.isCurrent()) return;
          const started = await post('start', {
            request: resolvedWorkRequest || q, deviceId: cfg.deviceId, os: window.electronAPI.platform, capability:opts.capability,
            workflow: discoveryRequest ? 'discovery' : researchRequest ? 'research' : 'review_milestone',
            conversationHistory: assistDialogueRef.current.slice(-16),
          });
          if (!started.taskId) throw new Error('Task did not start');
          if (!voiceTurn.isCurrent()) { await post('cancel', { taskId: started.taskId }); return; }
          assistWorkRef.current = { taskId: started.taskId, base };
          setReply('Task started.');
          // Surface the task's actual question/result in this conversation.
          // The desktop overlay alone is not a reliable delivery channel.
          await monitorTask(started.taskId);
          return;
        }
        taskHandled = false;
      } catch (e) {
        console.warn('[Assist task failed]', e.message);
        const message = /PERMISSION|SESSION|IDENTITY|FORBIDDEN/i.test(e.message)
          ? 'Computer control needs your signed-in account and Assist permission.'
          : /ASSIST_AUTOMATION_PAUSED/i.test(e.message)
          ? 'Web research is paused. This request did not start.'
          : 'I could not start or continue the task. No completed edit has been confirmed.';
        setReply(message); await speakError(message, voiceTurn); setError('');
        return;
      } finally { if (taskHandled) { voiceTurn.finish(); setLoading(false); } }
    }
    if (opts.diagnosticTest === true) {
      setReply('Transcript test: this request routes to conversation. An authenticated GPT response has not been requested because paid conversation tests are disabled.');
      voiceTurn.finish();
      return;
    }
    try {
    setActiveCommand(q);
    setVoiceInterim('');
    const normalized = normalizeQuestion(q);
    if (overrideQuestion != null) setQuestion(overrideQuestion);

    // ── Chain bookkeeping ──
    // A NEW user question (not isChainContinuation) starts a fresh chain:
    // remember the goal, reset the counter, cancel any in-flight follow-up.
    if (!opts.isChainContinuation) {
      chainAbortedRef.current = false;
      if (chainTimerRef.current) {
        clearTimeout(chainTimerRef.current);
        chainTimerRef.current = null;
      }
      chainGoalRef.current = q;
      chainStepRef.current = 0;

      // ── Leave Mode trigger detection (client-side, fast path) ──
      // User explicitly hands off. Aria switches to silent backend mode.
      // Examples: "leave mode", "just do it", "take care of it",
      //           "do it for me", "I'll let you handle it", "you handle it".
      // Backend can ALSO set leaveModeSuggested:true in its reply when it
      // senses fatigue / hands-off intent — Aria asks once, user accepts.
      const LEAVE_TRIGGER = /\b(leave\s*mode|go\s+silent|do\s+it\s+(for\s+me|yourself|on\s+your\s+own)|just\s+do\s+it|take\s+care\s+of\s+(it|this)|handle\s+(it|this)\s+(for\s+me|yourself)|i'?ll\s+let\s+you|you\s+handle|run\s+it\s+in\s+the\s+background)\b/i;
      const RESUME_TRIGGER = /\b(come\s+back|stop\s+leave|leave\s+off|exit\s+leave|are\s+you\s+(done|there)|status|what'?s\s+(the\s+)?(status|update))\b/i;
      if (LEAVE_TRIGGER.test(q)) {
        setLeaveMode(true);
        leaveModeRef.current = true;
        try {
          if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOverlayAssistMode === 'function') {
            window.electronAPI.sendOverlayAssistMode('leave');
          }
          if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOnScreenAnnotations === 'function') {
            window.electronAPI.sendOnScreenAnnotations({ annotations: [], message: '' });
          }
        } catch (_) {}
      } else if (RESUME_TRIGGER.test(q) && leaveModeRef.current) {
        setLeaveMode(false);
        leaveModeRef.current = false;
        try {
          if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOverlayAssistMode === 'function') {
            window.electronAPI.sendOverlayAssistMode('assist');
          }
        } catch (_) {}
      }
    } else {
      // continuation — increment step
      chainStepRef.current = (chainStepRef.current || 0) + 1;
    }

    const playAudio = (audioData, spokenText = '') => {
      if (!audioData || !voiceTurn.isCurrent()) return;
      // Clear any previous safety timeout
      if (ariaSpeakingTimeoutRef.current) { clearTimeout(ariaSpeakingTimeoutRef.current); ariaSpeakingTimeoutRef.current = null; }
      if (replyAudioRef.current) {
        try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
      }
      const audio = createAssistAudio(audioData);
      replyAudioRef.current = audio;
      audio.playbackRate = chooseAssistSpeechRate(spokenText);
      const tracePlayback = (stage) => {
        fetch(`${assistApiBase || getAssistApiBaseSync()}/api/pipeline/event`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage, meta: { inputMode: opts.inputMode, turnId: voiceTurn.id } }),
        }).catch(() => {});
      };
      audio.addEventListener('playing', () => tracePlayback('assist.audio.playing'), { once: true });
      audio.addEventListener('ended', () => tracePlayback('assist.audio.ended'), { once: true });
      audio.addEventListener('error', () => tracePlayback('assist.audio.error'), { once: true });
      // Estimate max duration from data URI size: ~1 sec per 24KB of base64 audio, with a 30s floor
      const estimatedBytes = typeof audioData === 'string' ? audioData.length : 0;
      const safetyMs = Math.max(30000, (estimatedBytes / 24000) * 1000 + 5000);
      const clearSpeaking = () => {
        if (ariaSpeakingTimeoutRef.current) { clearTimeout(ariaSpeakingTimeoutRef.current); ariaSpeakingTimeoutRef.current = null; }
        if (replyAudioRef.current === audio) replyAudioRef.current = null;
        setAriaSpeaking(false);
      };
      audio.addEventListener('play', () => {
        setAriaSpeaking(true);
        // Safety timeout: force ariaSpeaking=false if ended/error never fires
        ariaSpeakingTimeoutRef.current = setTimeout(clearSpeaking, safetyMs);
      });
      audio.addEventListener('ended', clearSpeaking);
      audio.addEventListener('error', clearSpeaking);
      voiceTurn.play(audio).catch(() => { clearSpeaking(); });
    };

    // ── Detect actionable command from user input ──
    const detectedAction = detectAction(q);

    // ── Direct desktop agent commands — execute immediately, no screen capture needed ──
    if (ASSIST_AUTOMATION_ENABLED && !opts.requireScreen && isDesktopApp && window.electronAPI?.executeCommand) {
      const ql = q.toLowerCase().trim();
      let directCmd = null;
      let confirmText = null;

      // Text with message: "text andrew saying hey" / "text andrew hey whats up"
      const textMatchMsg = q.match(/^(?:text|message|send\s+(?:a\s+)?(?:text|message|imessage|msg)\s+(?:to\s+)?)\s*(.+?)\s+(?:saying|that\s+says?|with(?:\s+message)?)\s+(.+)$/i)
        || q.match(/^(?:text|message)\s+(\S+)\s+(.+)$/i);
      if (textMatchMsg) {
        const to = textMatchMsg[1].replace(/^to\s+/i, '').trim();
        const msg = textMatchMsg[2].trim();
        if (to && msg) {
          directCmd = { type: 'send_imessage', to, message: msg };
          confirmText = `Sending that message to ${to}.`;
        }
      }
      // Text without message: "text andrew" / "send a text to andrew" / "message mom"
      if (!directCmd) {
        const textMatchNoMsg = q.match(/^(?:text|message|dm|imessage|send\s+(?:a\s+)?(?:text|message|imessage|msg)\s+(?:to\s+)?)\s*(.+?)$/i);
        if (textMatchNoMsg) {
          const to = textMatchNoMsg[1].replace(/^to\s+/i, '').trim();
          if (to && to.split(/\s+/).length <= 4) {
            directCmd = { type: 'send_imessage', to };
            confirmText = `Opening Messages to ${to}.`;
          }
        }
      }
      if (!directCmd) {
        const openMatch = ql.match(/^(?:open|launch|start)\s+(.+?)(?:\s+app)?$/i);
        if (openMatch) {
          const app = openMatch[1].replace(/\b\w/g, c => c.toUpperCase());
          directCmd = { type: 'open_app', app };
          confirmText = `Opening ${app}.`;
        }
      }
      if (!directCmd) {
        const volMatch = ql.match(/(?:set\s+)?volume\s+(?:to\s+)?(\d+)/i);
        if (volMatch) { directCmd = { type: 'set_volume', level: parseInt(volMatch[1]) }; confirmText = `Setting volume to ${volMatch[1]}.`; }
        else if (/\bmute\b/i.test(ql)) { directCmd = { type: 'set_volume', level: 0 }; confirmText = 'Muting.'; }
      }
      if (!directCmd && /\b(dark mode|dark theme)\b/i.test(ql)) {
        const enable = !/\b(off|disable|light)\b/i.test(ql);
        directCmd = { type: 'dark_mode', enable };
        confirmText = enable ? 'Turning on dark mode.' : 'Turning off dark mode.';
      }
      if (!directCmd && /\b(do not disturb|dnd|focus mode)\b/i.test(ql)) {
        const enable = !/\b(off|disable)\b/i.test(ql);
        directCmd = { type: 'do_not_disturb', enable };
        confirmText = enable ? 'Turning on Do Not Disturb.' : 'Turning off Do Not Disturb.';
      }

      if (directCmd) {
        setReply(confirmText);
        setAnnotations([]);
        setLoading(true);
        // Fire command execution and TTS in parallel for speed
        const [result, ttsRes] = await Promise.all([
          window.electronAPI.executeCommand(directCmd).catch(err => ({ ok: false, error: err.message })),
          fetchWithTimeout(`${assistApiBase || getAssistApiBaseSync()}/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: confirmText }),
          }, 8000).catch(() => null),
        ]);
        if (!result?.ok && result?.error) {
          confirmText = `I tried but got an error: ${result.error}`;
          setReply(confirmText);
        }
        try {
          if (ttsRes && ttsRes.ok) {
            const buf = await ttsRes.arrayBuffer();
            if (voiceTurn.isCurrent() && buf.byteLength > 100) {
              const bytes = new Uint8Array(buf);
              let binary = '';
              for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
              const dataUrl = `data:audio/wav;base64,${btoa(binary)}`;
              const audio = createAssistAudio(dataUrl);
              replyAudioRef.current = audio;
              audio.onplay = () => setAriaSpeaking(true);
              audio.onended = () => { if (replyAudioRef.current === audio) replyAudioRef.current = null; setAriaSpeaking(false); };
              voiceTurn.play(audio).catch(() => setAriaSpeaking(false));
            }
          }
        } catch (_) {}
        setLoading(false);
        return;
      }
    }


    const cache = responseCacheRef.current;
    // Persisted clips have no voice identity. Reuse text, synthesize in current voice.
    const cached = cache[normalized] ? { ...cache[normalized], audioData: null } : null;
    const greetingReply = greetingReplies[normalized];

    // If this is a pure action command, execute immediately and give short confirmation
    if (detectedAction && !opts.requireScreen) {
      const executed = executeAction(detectedAction);
      if (executed) {
        const confirmText = detectedAction.label ? `Opening ${detectedAction.label}.` : 'On it.';
        setReply(confirmText);
        setAnnotations([]);
        setLoading(true);
        try {
          const ttsRes = await fetchWithTimeout(`${assistApiBase || getAssistApiBaseSync()}/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: confirmText, ...ariaUserIdJsonBody() }),
            timeout: 10000,
          });
          if (ttsRes.ok) {
            const buf = await ttsRes.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
            if (audioData.length > 100) playAudio(audioData, confirmText);
          }
        } catch (_) {}
        setLoading(false);
        return;
      }
    }

    if (!opts.requireScreen && cached && cached.reply && cached.audioData) {
      setReply(cached.reply);
      setAnnotations(cached.annotations || []);
      playAudio(cached.audioData, cached.reply);
      return;
    }

    const instantReply = cached?.reply || greetingReply;
    if (instantReply && !opts.requireScreen) {
      setReply(instantReply);
      setAnnotations([]);
      setLoading(true);
      try {
        const ttsRes = await fetchWithTimeout(`${assistApiBase || getAssistApiBaseSync()}/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: instantReply, ...ariaUserIdJsonBody() }),
          timeout: 10000,
        });
        if (ttsRes.ok) {
          const buf = await ttsRes.arrayBuffer();
          const bytes = new Uint8Array(buf);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
          if (audioData.length > 100) {
            playAudio(audioData, instantReply);
            cache[normalized] = { reply: instantReply, audioData, annotations: [] };
            saveResponseCache(cache);
          }
        }
      } catch (_) {}
      setLoading(false);
      return;
    }

    // Detect conversational queries that have nothing to do with what's on screen.
    // Route these to the main conversation AI so the response always makes sense.
    // Screen-analysis AI is for "what's this on screen?" / "help me with this error" etc.
    // IMPORTANT: any query that implies an ACTION must go to screen analysis so Aria can execute it.
    const _ql = q.toLowerCase().replace(/[.!?,]+$/g, '').trim();
    // Action words = user wants Aria to DO something → always use screen analyzer
    const _isActionQuery = /\b(scroll|page down|page up|scan|virus|viruses|malware|refresh|reload|zoom|minimi[sz]e|text|send|open|click|type|point at|point to|point out|highlight|where is|where's|go to|navigate|search|set up|setup|sign in|log in|login|install|download|create|make|build|fix|close|delete|move|copy|paste|save|help me|order|buy|book|schedule|email|message|call|play|stop|start|run|deploy|check|find|show me|take me|bring up|turn on|turn off|enable|disable|connect|subscribe|pay|add|remove|upload|fill|submit|configure|update)\b/i.test(_ql);
    const _screenRead = opts.requireScreen && (!ASSIST_AUTOMATION_ENABLED || inspectionOnly || !/\b(click|type|scroll|delete|send|buy|install|download|save|close|move|open|run|submit|pay)\b/i.test(q));
    const _isConversational = !opts.requireScreen && (!ASSIST_AUTOMATION_ENABLED || !_isActionQuery);
    if (_isConversational) {
      console.log('[Assist] Conversational query detected — routing to main AI:', _ql);
    }

    // For action queries in desktop app, skip the slow screenshot capture — instant actions don't need it.
    // Only capture screenshot for non-action queries that actually need screen context.
    let base64 = null;
    if (opts.requireScreen && isDesktopApp) {
      try {
        const target = await window.electronAPI?.selectTaskScreen?.(q);
        if (target?.matched && target.app) {
          const focused = await window.electronAPI?.executeCommand?.({ type: 'focus_window', title: target.title || target.app });
          if (!focused?.ok) throw new Error('Could not focus the requested app');
        }
        const fresh = await window.electronAPI?.captureScreen?.({ includeClipboard: false });
        if (fresh?.ok && fresh.base64) base64 = fresh.base64;
      } catch (_) {
        if (voiceTurn.isCurrent()) {
          setReply('I could not bring the requested app into view. Please bring it forward so I can inspect it.');
          setLoading(false);
        }
        voiceTurn.finish();
        return;
      }
    }
    if (!base64 && (opts.requireScreen || (!_isConversational && !(_isActionQuery && isDesktopApp)))) {
      base64 = await waitForCaptureBase64();
    }
    if (opts.requireScreen && !base64) {
      if (voiceTurn.isCurrent()) {
        setReply('I could not read the shared screen yet. Please keep the app visible and try again.');
        setLoading(false);
      }
      voiceTurn.finish();
      return;
    }
    if (!voiceTurn.isCurrent()) return; else if (ASSIST_AUTOMATION_ENABLED && !opts.requireScreen && !_isConversational && _isActionQuery && isDesktopApp) {
      // Try a quick capture (no waiting) — if it's available great, if not don't block
      try { base64 = captureFrame(); } catch (_) {}
    }
    // If no screen capture but it's an action query, still hit the screen analyze endpoint.
    // The backend has instant action detection (text, email, search, open, etc.) that runs
    // BEFORE the image check — so it pattern-matches and executes without needing a screenshot.
    if (ASSIST_AUTOMATION_ENABLED && !base64 && _isActionQuery && isDesktopApp) {
      console.log('[Assist] Action query — skipping screenshot, sending to screen analyze for instant action detection');
      // Fall through to screen analyze path — instant actions don't need an image
    } else if (!base64 || _screenRead) {
      // Truly conversational query with no screenshot — use conversation endpoint
      console.log('[Assist] No screen capture available, sending as conversation query');
      try {
        setLoading(true);
        const base = assistApiBase || getAssistApiBaseSync();
        assistAbortRef.current?.abort();
        const conversationController = new AbortController();
        assistAbortRef.current = conversationController;
        const convData = await streamNormalChat({
          url: `${base}/api/ai/normal-chat-stream`,
          body: { initialQuery: q, prompt: q, assistReadOnly: !ASSIST_AUTOMATION_ENABLED, assistControlledTasks: ASSIST_CONTROLLED_TASKS_ENABLED && isDesktopApp, ...(_screenRead ? { screenImage: base64 } : {}), inputMode: opts.inputMode === 'voice' ? 'voice' : 'text', conversationHistory: assistDialogueRef.current.slice(-16), skipAudio: true, ...ariaUserIdJsonBody(), preferredName },
          turnId: `assist-chat-${Date.now()}`,
          signal: conversationController.signal,
        });
        const convRes = { ok: true, status: 200 };
        if (!voiceTurn.isCurrent()) return;
        if (convData.reply) assistDialogueRef.current = [...assistDialogueRef.current, { role: 'user', content: q }, { role: 'assistant', content: convData.reply }].slice(-16);
        if (convRes.ok && convData.reply) {
          setReply(convData.reply);
          // Pick up action cards from backend response
          setActionCards(convData.actionCards || []);
          // Also execute the detected action (e.g., open the URL) if present
          if (detectedAction) executeAction(detectedAction);
          if (playbackMutedRef.current) return;
          if (convData.audioData) {
            const audioSrc = convData.audioData.startsWith('data:') ? convData.audioData : `data:audio/wav;base64,${convData.audioData}`;
            playAudio(audioSrc, convData.reply);
          } else {
            // Generate TTS for the reply
            try {
              const ttsRes = await fetchWithTimeout(`${base}/tts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: convData.reply, ...ariaUserIdJsonBody() }),
                timeout: 10000,
              });
              if (ttsRes.ok) {
                const buf = await ttsRes.arrayBuffer();
                const bytes = new Uint8Array(buf);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
                if (audioData.length > 100) playAudio(audioData, convData.reply);
              }
            } catch (_) {}
          }
        } else {
          // Parse specific error types and speak them
          const errMsg = convData.error || convData.message || '';
          if (convRes.status === 402 || /credit|subscription|limit|quota/i.test(errMsg)) {
            await speakError("You've hit your usage limit. Add more in your account settings to keep using Aria.", voiceTurn);
          } else if (convRes.status === 429 || /rate.?limit|too many/i.test(errMsg)) {
            await speakError("I'm getting too many requests right now. Give me a sec and try again.", voiceTurn);
          } else if (convRes.status === 401 || /auth|unauthorized|sign.?in/i.test(errMsg)) {
            await speakError("You need to sign in for this to work.", voiceTurn);
          } else {
            await speakError("Something went wrong on my end. Try again in a moment.", voiceTurn);
          }
        }
      } catch (e) {
        const msg = e.message || '';
        if (/timeout|timed out/i.test(msg)) {
          await speakError("That took too long. Check your connection and try again.", voiceTurn);
        } else if (/network|fetch|connect|ECONNREFUSED/i.test(msg)) {
          await speakError("I can't reach the server. Make sure the backend is running.", voiceTurn);
        } else {
          await speakError("Something went wrong. Try again.", voiceTurn);
        }
      } finally {
        voiceTurn.finish();
        setLoading(false);
      }
      return;
    }
    if (!voiceTurn.isCurrent()) return;
    assistAbortRef.current?.abort();
    const assistController = new AbortController();
    assistAbortRef.current = assistController;
    const reqId = ++assistRequestIdRef.current;

    setLoading(true);
    setError('');
    setReply('');
    setAnsweredByModel('');
    setAnnotations([]);
    setActionCards([]);

    try {
      // Always resolve latest device id before Assist requests so the backend can queue desktop actions (race: first paint before getAgentConfig).
      let deviceIdForAssist = (agentDeviceId || '').trim();
      if (typeof window !== 'undefined' && window.electronAPI?.getAgentConfig) {
        try {
          const cfg = await window.electronAPI.getAgentConfig();
          const id = cfg?.deviceId && String(cfg.deviceId).trim();
          if (id) {
            deviceIdForAssist = id;
            setAgentDeviceId(id);
            try {
              localStorage.setItem('shimmer_agent_device_id', id);
            } catch (_) {}
          }
        } catch (_) {}
      } else if (!deviceIdForAssist && typeof localStorage !== 'undefined') {
        try {
          const stored = localStorage.getItem('shimmer_agent_device_id');
          if (stored && stored.trim()) deviceIdForAssist = stored.trim();
        } catch (_) {}
      }

      if (!voiceTurn.isCurrent()) return;
      try {
        if (window.electronAPI?.workScreen) screenInfoRef.current = await window.electronAPI.workScreen();
      } catch (_) {}
      const fullPayload = {
        os: (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.platform) || '',
        // How many screens he has, and which one he already picked (8/23).
        // The backend asks "which screen do you want me to use?" the first
        // time there is more than one, with a big number painted on each.
        ...(screenInfoRef.current ? { screenCount: screenInfoRef.current.count, workScreen: screenInfoRef.current.chosen } : {}),
        // What is actually running on his machine (8/28). The windows he can
        // see, and which one he has been working in. Without this she had three
        // strings a turn and the UIA tree of the front window only — so "that
        // window" or "the one I was just in" meant nothing to her.
        ...(machineCtxRef.current ? { machineContext: machineCtxRef.current } : {}),
        image: base64,
        question: opts.requireScreen ? `Inspect the shared screen and existing work before asking intake questions. Briefly explain what is already present and the next useful step for this user goal: ${q}. Ask at most one question, only if its answer cannot be learned from the visible project. Do not assume the project is new or ask the user to choose a style before looking.` : q,
        screenShareMode: true,
        ...(isDesktopApp ? { desktopAssist: true } : {}),
        ...(deviceIdForAssist ? { deviceId: deviceIdForAssist } : {}),
        ...ariaUserIdJsonBody(),
        ...(preferredName ? { preferredName } : {}),
        // ── Chain + leave-mode telemetry context ──
        // The backend's userDetective hook reads these to attribute the
        // turn correctly: was this a fresh user question or step N of an
        // auto-chain? Was the user in Leave Mode while this happened?
        isChainContinuation: !!opts.isChainContinuation,
        chainStep: chainStepRef.current || 0,
        chainGoal: chainGoalRef.current || '',
        leaveMode: !!leaveModeRef.current,
        // ── Ambient awareness context ──
        screenContext: screenContextRef.current || {},
        recentActions: recentActionsRef.current || [],
        hasPreviousFrame: ambientFramesRef.current.length > 1,
        previousFrameSummary: ambientFramesRef.current.length > 1 ? {
          timestamp: ambientFramesRef.current[ambientFramesRef.current.length - 2]?.timestamp,
          msSinceCapture: Date.now() - (ambientFramesRef.current[ambientFramesRef.current.length - 2]?.timestamp || Date.now()),
        } : null,
        // If we have a previous ambient frame, include it for change detection
        previousImage: ambientFramesRef.current.length > 1
          ? ambientFramesRef.current[ambientFramesRef.current.length - 2].base64
          : undefined,
      };
      // WHICH OS (8/22): the server's instant shortcuts were all Mac keys and
      // landed on the Windows key here. Every analyze call says what it runs on.
      const _os = (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.platform) || '';
      const quickPayload = { question: q, quickOnly: true, os: _os, ...(deviceIdForAssist ? { deviceId: deviceIdForAssist } : {}), ...ariaUserIdJsonBody(), ...(preferredName ? { preferredName } : {}) };

      let quickAudioPlayed = false;
      const base = assistApiBase || getAssistApiBaseSync();
      if (!base) {
        await speakError("I can't find the backend server. Make sure it's running on port 3001.", voiceTurn);
        setLoading(false);
        return;
      }

      // ── PREAMBLE — fire the moment Assist activates so Aria isn't silent
      // while the slow vision analyze runs. /api/screen/begin returns:
      //   { text: "Alright. Let me see what you're working with.",
      //     episodeId: "...", isContinuing: true|false }
      // Text is spoken/shown immediately. episodeId threads into fullPayload
      // so analyzeAndAct tracks actions against the right work episode.
      let episodeIdForAssist = assistEpisodeRef.current;
      // Set when /screen/begin asks which screen to work on, so this turn ends
      // on the question instead of reading a screen he has not chosen yet.
      let askedScreenThisTurn = false;
      try {
        if (!assistEpisodeRef.current) {
        const beginRes = await fetchWithTimeout(`${base}/api/screen/begin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userMessage: q,
            deviceId: deviceIdForAssist,
            ...ariaUserIdJsonBody(),
            // How many screens he has, so /screen/begin can ask WHICH ONE the
            // moment Assist opens (8/28). The ask used to live inside
            // /screen/analyze, where a middleware answered first and it never
            // ran — he got "I can see Settings but it is not telling me what
            // is on screen" instead of the question, on a 3-monitor machine.
            ...(screenInfoRef.current ? {
              screenCount: screenInfoRef.current.count,
              workScreen: screenInfoRef.current.chosen,
              targetScreenMatched: screenInfoRef.current.targetScreenMatched === true,
            } : {}),
            // appsVisible: best-effort hint from screen context (windows/tabs)
            appsVisible: (() => {
              try {
                const sc = screenContextRef.current || {};
                const apps = [];
                if (sc.activeApp) apps.push(String(sc.activeApp).toLowerCase());
                if (sc.activeWindow) apps.push(String(sc.activeWindow).toLowerCase());
                if (sc.frontmostUrl) apps.push(String(sc.frontmostUrl).replace(/^https?:\/\//, '').split('/')[0]);
                return [...new Set(apps)].slice(0, 5);
              } catch (_) { return []; }
            })(),
          }),
          signal: assistController.signal,
          timeout: 3000,
        });
        const beginData = await beginRes.json().catch(() => ({}));
        if (beginRes.ok && beginData.text) {
          setReply(beginData.text);
          episodeIdForAssist = beginData.episodeId || null;
          assistEpisodeRef.current = episodeIdForAssist;
        }
        // ── SHE ASKED WHICH SCREEN — SO WAIT FOR THE ANSWER (8/28) ────────
        //
        // Without this the question was asked and then immediately talked
        // over: the analyze call ran anyway and its answer replaced the
        // question on screen. Measured on his 3-monitor box — he saw
        // "I can see Settings but it is not telling me what is on screen"
        // and never saw a question at all.
        //
        // Asking and then not waiting is worse than not asking.
        if (beginRes.ok && beginData.askedScreen) {
          askedScreenThisTurn = true;
          try { await window.electronAPI?.screenNumbers?.(true); } catch (_) {}
        }
        }
      } catch (e) {
        // Preamble is non-critical — vision call still proceeds without it
        console.warn('[Assist] /screen/begin failed (non-fatal):', e?.message || e);
      }
      // Her question IS this turn's answer. The numbers are up; his reply
      // ("2", "screen 3", "the middle one") comes back through the normal
      // path, where /api/screen/analyze already knows how to read it.
      if (askedScreenThisTurn || !voiceTurn.isCurrent()) return;
      // Thread episodeId into the full analyze payload
      if (episodeIdForAssist) {
        fullPayload.episodeId = episodeIdForAssist;
      }

      const quickPromise = opts.requireScreen ? Promise.resolve() : fetchWithTimeout(`${base}/api/screen/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quickPayload),
        signal: assistController.signal,
        timeout: 15000,
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (voiceTurn.isCurrent() && res.ok && data.quickReply) {
            setReply(data.quickReply);
            if (data.audioData) {
              playAudio(data.audioData, data.quickReply || q);
              quickAudioPlayed = true;
            }
          } else if (!res.ok) {
            console.warn('[Assist] quickOnly failed:', res.status, data?.error || data?.message);
          }
        })
        .catch((err) => {
          console.warn('[Assist] quickOnly request failed:', err?.message || err);
        });

      let fullRes;
      try {
        fullRes = await fetchWithTimeout(`${base}/api/screen/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fullPayload),
          signal: assistController.signal,
          timeout: 225000,
        });
      } catch (e) {
        await quickPromise.catch(() => {});
        throw e;
      }
      if (!voiceTurn.isCurrent()) return;
      let data = {};
      try {
        data = await fullRes.json();
      } catch (_) {
        throw new Error(fullRes.ok ? 'Invalid response from server' : `Error ${fullRes.status}`);
      }
      await quickPromise;
      if (!voiceTurn.isCurrent()) return;
      if (!fullRes.ok) {
        if (fullRes.status === 402) {
          await speakError("You've hit your usage limit. Add more in your account settings to keep using Aria.", voiceTurn);
          return;
        }
        if (fullRes.status === 429) {
          await speakError("I'm getting too many requests right now. Give me a sec and try again.", voiceTurn);
          return;
        }
        if (fullRes.status === 401) {
          await speakError("You need to sign in for this to work.", voiceTurn);
          return;
        }
        const msg = data.error || data.message || fullRes.statusText;
        throw new Error(msg);
      }
      const replyText = data.reply || '';
      if (replyText) assistDialogueRef.current = [...assistDialogueRef.current, { role: 'user', content: q }, { role: 'assistant', content: replyText }].slice(-16);
      const ann = data.annotations || [];
      setReply(replyText);
      setAnsweredByModel(data.model || '');
      // Leave Mode suppresses on-screen circles + caption — Aria is working
      // silently in the background. We still keep `annotations` in state for
      // logging / cache / debugging, but skip pushing to the overlay window.
      setAnnotations(leaveModeRef.current ? [] : ann);

      // ── HOW-TO assist cues — Aria highlights + moves cursor while she
      // explains. The backend ships cues in the response when the user
      // asked a how-to question. We dispatch them to the main process
      // (which calls agent.js highlight_label / move_cursor_to_label).
      // Skip when in Leave Mode — visual cues are explicitly off then.
      const assistCues = Array.isArray(data.assistCues) ? data.assistCues : null;
      if (assistCues && assistCues.length > 0 && !leaveModeRef.current) {
        if (window.electronAPI && typeof window.electronAPI.executeAssistCues === 'function') {
          // Stagger 1.5s between cues so the visual lines up with Aria's voice
          window.electronAPI.executeAssistCues(assistCues, { staggerMs: 1500 })
            .catch(err => console.warn('[assistCues] dispatch failed:', err));
        } else {
          console.warn('[assistCues] electronAPI.executeAssistCues not available — running in browser?');
        }
      }

      // ── PERMISSION WALKTHROUGH ────────────────────────────────────
      // If the canonical ships an openSettingsAction (screen recording,
      // accessibility, microphone, or all-of-privacy), open that
      // Settings pane automatically. Aria says "I'm opening Settings
      // for you" as step 1, then the panel pops up at the exact right
      // place. User doesn't have to navigate.
      const settingsAction = data.openSettingsAction;
      if (settingsAction && window.electronAPI) {
        const handler = window.electronAPI[settingsAction];
        if (typeof handler === 'function') {
          // Tiny delay so Aria's voice gets a half-beat before the
          // panel opens — feels less abrupt than instant pop-up.
          setTimeout(() => {
            try { handler(); }
            catch (err) { console.warn('[openSettingsAction] failed:', settingsAction, err); }
          }, 600);
        } else {
          console.warn('[openSettingsAction] electronAPI.' + settingsAction + ' not available');
        }
      }
      setActionCards(data.actionCards || []);
      // Leave Mode = no narration. Cache reply text + (server-side) audio
      // anyway so when Leave exits we can replay. Don't actually play audio.
      if (data.audioData) {
        if (!quickAudioPlayed && !leaveModeRef.current) {
          playAudio(data.audioData, replyText || q);
        }
        cache[normalized] = { reply: replyText, audioData: data.audioData, annotations: ann };
        saveResponseCache(cache);
      } else if (!quickAudioPlayed && !leaveModeRef.current && replyText && replyText.trim().length > 0) {
        // No audio came back from the analyze call (skipAudio path, OpenAI key
        // missing, transient TTS failure, etc.) — but we have reply text and
        // Aria is supposed to be NARRATING in Assist Mode, never silent.
        // Fetch /tts ourselves the same way the conversation fallback does.
        try {
          const ttsRes = await fetchWithTimeout(`${base}/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: replyText, ...ariaUserIdJsonBody() }),
            timeout: 10000,
          });
          if (ttsRes.ok) {
            const buf = await ttsRes.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
            if (audioData.length > 100) {
              playAudio(audioData, replyText);
              cache[normalized] = { reply: replyText, audioData, annotations: ann };
              saveResponseCache(cache);
            } else {
              setAriaSpeaking(false);
            }
          } else {
            setAriaSpeaking(false);
          }
        } catch (_) {
          setAriaSpeaking(false);
        }
      } else if (!quickAudioPlayed) {
        setAriaSpeaking(false);
      }

      if (!voiceTurn.isCurrent()) return;

      // ── Auto-chain: schedule the next step capture ──
      // Conditions to KEEP chaining:
      //   1. We have annotations (= Aria pointed at something to do).
      //   2. The chain hasn't been aborted by user input.
      //   3. We're under MAX_CHAIN_TURNS (safety cap).
      //   4. Reply doesn't sound like the task is finished.
      // Auto-recapture after CHAIN_FOLLOWUP_MS so the user has time to act.
      try {
        const TASK_DONE = /\b(all set|all done|that's it|task complete|you're done|you are done|finished|that's everything|good to go)\b/i;
        const taskDone = TASK_DONE.test(replyText || '');
        const shouldChain =
          data.continueTask === true &&
          ann.length > 0 &&
          !chainAbortedRef.current &&
          !taskDone &&
          (chainStepRef.current || 0) < MAX_CHAIN_TURNS &&
          isDesktopApp; // chain only matters where Aria can actually point
        if (chainTimerRef.current) {
          clearTimeout(chainTimerRef.current);
          chainTimerRef.current = null;
        }
        if (shouldChain) {
          chainTimerRef.current = setTimeout(async () => {
            chainTimerRef.current = null;
            if (chainAbortedRef.current) return;
            const goal = chainGoalRef.current || q;
            const stepNum = (chainStepRef.current || 0) + 1;
            const followUp =
              `Continue helping me with: "${goal}". Look at my screen now. ` +
              (stepNum === 1
                ? 'If I completed the previous step, narrate the next one and circle where to click. If I didn\'t, briefly remind me, no scolding.'
                : 'Confirm I completed the previous step in one short phrase, then narrate the next step and circle where to click.') +
              ' If the task is fully done, say so plainly and stop.';
            const send = sendQuestionRef.current;
            if (typeof send === 'function') {
              try { await send(followUp, { isChainContinuation: true }); } catch (_) { /* swallow */ }
            }
          }, CHAIN_FOLLOWUP_MS);
        } else if (taskDone && leaveModeRef.current) {
          // Leave Mode + task done — Aria comes back to speak the result.
          setLeaveMode(false);
          const finishResultSpeech = voiceTurn.retain();
          setTimeout(async () => {
            try {
              if (replyText && voiceTurn.isCurrent()) {
                // Re-trigger TTS for the final result so the user hears it.
                await fetchWithTimeout(`${base}/tts`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ text: replyText, ...ariaUserIdJsonBody() }),
                  timeout: 10000,
                }).then(async (r) => {
                  if (!r.ok) return;
                  const buf = await r.arrayBuffer();
                  const bytes = new Uint8Array(buf);
                  let binary = '';
                  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                  const audioData = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
                  if (audioData.length > 100) playAudio(audioData, replyText);
                }).catch(() => {});
              }
            } catch (_) {} finally { finishResultSpeech(); }
          }, 100);
        }
      } catch (_) { /* chain scheduling is best-effort */ }
    } catch (e) {
      if (e?.name === 'AbortError' || String(e?.message || '').includes('aborted')) {
        return;
      }
      const msg = e.message || '';
      if (/timeout|timed out/i.test(msg)) {
        await speakError("That took too long. Check your connection and try again.", voiceTurn);
      } else if (/network|fetch|connect|ECONNREFUSED/i.test(msg)) {
        await speakError("I can't reach the server. Make sure the backend is running.", voiceTurn);
      } else {
        await speakError(msg || "Something went wrong. Try again.", voiceTurn);
      }
    } finally {
      if (assistRequestIdRef.current === reqId) {
        setLoading(false);
      }
    }
    } finally {
      voiceTurn.finish();
    }
  }, [question, agentDeviceId, captureFrame, waitForCaptureBase64, isDesktopApp, assistApiBase, speakError, voiceTurns]);

  sendQuestionRef.current = sendQuestion;

  const SpeechRecognitionAPI = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Mic must respect both the Assist mic button (micActive) and overlay mute from the main window.
  const micOn = micActive && !overlayMuted && !hostOwnsMic;
  const micOnRef = useRef(micOn);
  micOnRef.current = micOn;
  const ariaSpeakingRef = useRef(ariaSpeaking);
  ariaSpeakingRef.current = ariaSpeaking;

  // Voice recognition — VAD + Whisper (same as home screen, more reliable than browser SpeechRecognition)
  // Records audio when user speaks, sends to backend /api/speech/transcribe for Whisper transcription
  const VAD_START_RMS = 0.012;
  const VAD_END_RMS = 0.003;
  const VAD_SILENCE_MS = 1100;  // brief answers: "yes", "the blue one"
  // Assist mode gets the same rule as the main mic (9/5): 1100ms cuts a
  // sentence with a think in the middle of it, because real mid-sentence
  // pauses were MEASURED at 1600ms in the 9/1 ear audit. Short utterance
  // keeps the snappy wait; a real sentence gets the measured one.
  const VAD_SILENCE_LONG_MS = 1600;
  const VAD_MIDSENTENCE_AFTER_MS = 1500;
  const VAD_MIN_MS = 400;
  const VAD_MAX_MS = 20000;     // Allow up to 20s for longer instructions

  useEffect(() => {
    if (!micOn) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setSpeechError('Microphone not supported.');
      speakError("I can't access a microphone on this device. You can still type your questions.");
      return;
    }
    setSpeechError('');
    let cancelled = false;
    let micStream = null;
    let audioCtx = null;
    let analyser = null;
    let rafId = null;

    (async () => {
      // Get mic stream
      try {
        console.log('[Assist-Voice] Requesting mic...');
        micStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        micPermissionGrantedRef.current = true;
        console.log('[Assist-Voice] Mic stream acquired');
      } catch (permErr) {
        console.error('[Assist-Voice] Mic denied:', permErr.message);
        if (!cancelled) {
          setSpeechError(desktopMicDeniedMessage());
          speakError("Microphone access was denied. Allow mic for Aria in your system settings, then try again.");
        }
        return;
      }
      if (cancelled) { micStream.getTracks().forEach(t => t.stop()); return; }

      // Set up audio analysis
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(micStream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.3;
        source.connect(analyser);
      } catch (e) {
        console.error('[Assist-Voice] AudioContext failed:', e.message);
        if (!cancelled) {
          setSpeechError('Audio setup failed.');
          speakError("Audio setup failed. Try restarting Aria.");
        }
        return;
      }

      setListening(true);
      console.log('[Assist-Voice] Listening with VAD + Whisper');

      // Pick a recording MIME type
      const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
      let recorderMime = '';
      for (const m of mimeTypes) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) { recorderMime = m; break; }
      }
      if (!recorderMime && typeof MediaRecorder !== 'undefined') recorderMime = '';

      // VAD state
      let recording = false;
      let recorder = null;
      let chunks = [];
      let recordStart = 0;
      let silenceStart = 0;
      let noiseFloor = 0.005;
      let noiseFloorSamples = 0;
      // Peak energy of the current capture — fan hum / transient clicks cross
      // the START threshold but never reach speech loudness. Whisper then
      // HALLUCINATES words ("thank you", "bye") from that noise and Aria
      // answers them — the phantom TTS (owner bug 2026-07-15). We track the
      // peak and refuse to transcribe captures that never resembled speech.
      let peakRMS = 0;
      let lastPeakRMS = 0;
      // Set when Aria's own reply started playing while a capture was rolling —
      // that recording CONTAINS HER VOICE. Transcribing it made her answer
      // herself in a loop ("Hey." -> mic hears it -> "Hi." -> "Hey." …).
      let capturePolluted = false;
      let lastPolluted = false;
      const timeDomain = new Float32Array(analyser.fftSize);

      function getRMS() {
        analyser.getFloatTimeDomainData(timeDomain);
        let sum = 0;
        for (let i = 0; i < timeDomain.length; i++) sum += timeDomain[i] * timeDomain[i];
        return Math.sqrt(sum / timeDomain.length);
      }

      function startRecording() {
        if (recording || cancelled) return;
        recording = true;
        recordStart = Date.now();
        peakRMS = 0;
        capturePolluted = false;
        chunks = [];
        console.log('[Assist-Voice] Recording started');
        setVoiceInterim('Listening...');
        setMicSpeaking(true);

        // Interrupt Aria if she's speaking — and mark the capture: it will
        // contain her tail audio, so it must never be transcribed as the user.
        if (ariaSpeakingRef.current) {
          capturePolluted = true;
          const playing = replyAudioRef.current;
          if (playing) { try { playing.pause(); playing.currentTime = 0; } catch (_) {} replyAudioRef.current = null; }
          setAriaSpeaking(false);
          // Restore system audio since Aria was interrupted
          if (window.electronAPI?.restoreSystemAudio) window.electronAPI.restoreSystemAudio().catch(() => {});
        }

        try {
          recorder = new MediaRecorder(micStream, recorderMime ? { mimeType: recorderMime } : undefined);
          recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
          recorder.onstop = async () => {
            if (cancelled || chunks.length === 0) return;
            const blob = new Blob(chunks, { type: recorderMime || 'audio/webm' });
            chunks = [];
            if (blob.size < 1000) { console.log('[Assist-Voice] Recording too small, skipping'); return; }
            if (lastPolluted) {
              console.log('[Assist-Voice] Capture overlapped Aria speaking — discarding (would transcribe her own voice)');
              setVoiceInterim('');
              return;
            }
            // ENERGY GATE: the capture never got anywhere near speech loudness
            // (fan hum / a click crossed the start threshold). Whisper would
            // hallucinate words from this — don't even transcribe it.
            if (lastPeakRMS < noiseFloor * 3.5) {
              console.log('[Assist-Voice] Capture peak ' + lastPeakRMS.toFixed(4) + ' ~ noise floor — skipping (no speech)');
              setVoiceInterim('');
              return;
            }

            // Convert to base64 and send to Whisper
            try {
              const buf = await blob.arrayBuffer();
              const bytes = new Uint8Array(buf);
              let binary = '';
              for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
              const base64 = btoa(binary);
              const mime = recorderMime || 'audio/webm';

              console.log('[Assist-Voice] Sending to Whisper (' + Math.round(blob.size / 1024) + 'KB)...');
              setVoiceInterim('Processing...');

              const apiBase = assistApiBase || getAssistApiBaseSync();
              const res = await fetchWithTimeout(`${apiBase}/api/speech/transcribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audio: base64, mime }),
                timeout: 30000,
              });
              const data = await res.json().catch(() => ({}));

              if (res.ok && data.ok && data.text && data.text.trim()) {
                const text = data.text.trim();
                console.log('[Assist-Voice] Whisper transcript:', text);
                setVoiceInterim('');
                if (text.length >= 2) {
                  // Handle casual/social/test phrases locally — don't send to the screen assist AI
                  // which has no context for conversation and gives nonsensical replies like "I'm working on it."
                  const lc = text.toLowerCase().replace(/[.!?,]+$/g, '').trim();
                  // WHISPER HALLUCINATION FILTER: on borderline-quiet captures,
                  // whisper invents exactly these ("thank you", "bye", "you"…) —
                  // and Aria was ANSWERING them out of nowhere. A real user
                  // saying "thanks" speaks at speech loudness and passes.
                  const HALLUC = /^(thank(s| you)( for watching)?|you|bye( ?bye)?|okay|ok|uh+m?|hm+|yeah|so|the end|1|2|\W*)$/i;
                  if (HALLUC.test(lc) && lastPeakRMS < noiseFloor * 6) {
                    console.log('[Assist-Voice] Dropping likely whisper hallucination on quiet capture:', JSON.stringify(lc));
                    return;
                  }
                  // Short greetings/one-worders must be SPOKEN, not fan wash:
                  // real speech at the mic peaks well above 0.025 RMS. Whisper
                  // kept turning sustained fan noise into "Hi." and Aria kept
                  // answering "Hey." to an empty room.
                  if (lc.length <= 8 && lastPeakRMS < Math.max(0.025, noiseFloor * 6)) {
                    console.log('[Assist-Voice] Short transcript on low-energy capture — dropping:', JSON.stringify(lc), 'peak', lastPeakRMS.toFixed(4));
                    return;
                  }
                  const INSTANT = {
                    'can you hear me': 'Yeah, I hear you.',
                    'can you hear me okay': 'Loud and clear.',
                    'can you hear me now': 'Yep, loud and clear.',
                    'do you hear me': 'Yeah, I hear you.',
                    'are you there': "I'm here.",
                    'you there': "Yep, I'm here.",
                    'hello': 'Hey.',
                    'hi': 'Hey.',
                    'hey': 'Hey.',
                    'testing': 'Working.',
                    'test': 'All good.',
                    'are you listening': "Yeah, I'm listening.",
                    'how are you': 'Doing good.',
                    'thanks': 'Anytime.',
                    'thank you': 'Anytime.',
                    'bye': 'Later.',
                    'never mind': 'Got it.',
                    'say something': "I'm here.",
                    'talk to me': "I'm listening. What's up?",
                  };
                  let instantReply = INSTANT[lc] || null;
                  if (!instantReply && /^(can you|do you|could you) hear me/i.test(lc)) instantReply = 'Yeah, I hear you.';
                  if (!instantReply && /^(are you|you) (there|listening|awake)/i.test(lc)) instantReply = "I'm here.";
                  if (!instantReply && /^test(ing)?\s*$/i.test(lc)) instantReply = 'Working.';
                  if (instantReply) {
                    const instantTurn = voiceTurns.begin('assist');
                    console.log('[Assist-Voice] Instant reply:', instantReply);
                    setReply(instantReply);
                    try {
                      const _base = assistApiBase || getAssistApiBaseSync();
                      const _tts = await fetchWithTimeout(`${_base}/tts`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ text: instantReply, ...ariaUserIdJsonBody() }),
                        timeout: 8000,
                      });
                      if (_tts.ok) {
                        const _buf = await _tts.arrayBuffer();
                        if (cancelled || !instantTurn.isCurrent()) return;
                        const _bytes = new Uint8Array(_buf);
                        let _bin = ''; for (let _i = 0; _i < _bytes.length; _i++) _bin += String.fromCharCode(_bytes[_i]);
                        const _url = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(_bin) : ''}`;
                        if (_url.length > 100) {
                          if (replyAudioRef.current) { try { replyAudioRef.current.pause(); } catch (_) {} }
                          const _a = createAssistAudio(_url);
                          replyAudioRef.current = _a;
                          // Single-handler properties — auto-cleanup with the Audio element.
                          _a.onplay = () => setAriaSpeaking(true);
                          _a.onended = () => { if (replyAudioRef.current === _a) replyAudioRef.current = null; setAriaSpeaking(false); };
                          _a.onerror = () => { if (replyAudioRef.current === _a) replyAudioRef.current = null; setAriaSpeaking(false); };
                          instantTurn.play(_a).catch(() => setAriaSpeaking(false));
                        }
                      }
                    } catch (_) {} finally { instantTurn.finish(); }
                    return;
                  }
                  const send = sendQuestionRef.current;
                  if (send) send(text);
                }
              } else {
                console.log('[Assist-Voice] No transcript from Whisper:', data?.error || 'empty');
                setVoiceInterim('');
              }
            } catch (e) {
              console.error('[Assist-Voice] Whisper failed:', e.message);
              setVoiceInterim('');
              // Only speak if it's a connection issue, not a transient fail
              if (/network|fetch|connect|ECONNREFUSED/i.test(e.message || '')) {
                speakError("I can't reach the server to process your voice. Check your connection.");
              }
            }
          };
          recorder.start(100); // collect in 100ms chunks
        } catch (e) {
          console.error('[Assist-Voice] MediaRecorder failed:', e.message);
          recording = false;
          setMicSpeaking(false);
        }
      }

      function stopRecording() {
        if (!recording || !recorder) return;
        recording = false;
        lastPeakRMS = peakRMS;   // snapshot for the async onstop handler
        lastPolluted = capturePolluted;
        setMicSpeaking(false);
        const duration = Date.now() - recordStart;
        console.log('[Assist-Voice] Recording stopped (' + duration + 'ms)');
        if (duration < VAD_MIN_MS) {
          // Too short — discard
          try { recorder.stop(); } catch (_) {}
          chunks = [];
          setVoiceInterim('');
          return;
        }
        try { if (recorder.state === 'recording') recorder.stop(); } catch (_) {}
      }

      // VAD loop
      let ariaDoneWaitUntil = 0; // suppress VAD for a moment after Aria finishes speaking
      function vadLoop() {
        if (cancelled) return;
        // Drive with setTimeout, NOT requestAnimationFrame: Assist mode hides the
        // main window (mainWindow.hide()), and Chromium FREEZES rAF callbacks for a
        // hidden window — which froze this VAD loop so the mic never started
        // recording ("Aria doesn't listen in assist mode"). setTimeout keeps ticking
        // while hidden, matching the primary useVoiceCapture hook.
        rafId = setTimeout(vadLoop, 30);
        // A hidden window can also auto-suspend the AudioContext — keep it live so
        // getRMS() reads real levels.
        if (audioCtx && audioCtx.state === 'suspended') { try { audioCtx.resume(); } catch (_) {} }

        // Don't record while Aria is speaking (echo cancellation)
        if (ariaSpeakingRef.current) {
          if (recording) { capturePolluted = true; stopRecording(); }
          ariaDoneWaitUntil = Date.now() + 900; // tail bleed cushion after she stops
          return;
        }
        // Brief cooldown after Aria finishes to avoid picking up tail-end audio
        if (Date.now() < ariaDoneWaitUntil) return;

        const rms = getRMS();

        // Build noise floor from first ~30 frames
        if (noiseFloorSamples < 30) {
          noiseFloor = (noiseFloor * noiseFloorSamples + rms) / (noiseFloorSamples + 1);
          noiseFloorSamples++;
          return;
        }

        const dynamicThreshold = Math.max(VAD_END_RMS, noiseFloor * 1.6);
        const startThreshold = Math.max(VAD_START_RMS, noiseFloor * 2.5);

        if (!recording) {
          // ADAPTIVE FLOOR: fans ramp up and down (mining rig!) — a floor
          // calibrated once at startup goes stale and lets fan wash trigger
          // captures all day. Track the ambient slowly while idle.
          if (rms < startThreshold) noiseFloor = noiseFloor * 0.98 + rms * 0.02;
          if (rms > startThreshold) {
            startRecording();
            silenceStart = 0;
          }
        } else {
          // Check for max duration
          if (rms > peakRMS) peakRMS = rms;   // track how loud this capture got
          if (Date.now() - recordStart > VAD_MAX_MS) {
            stopRecording();
            return;
          }
          // Check for silence
          if (rms < dynamicThreshold) {
            if (!silenceStart) silenceStart = Date.now();
            else if (Date.now() - silenceStart > (Date.now() - recordStart >= VAD_MIDSENTENCE_AFTER_MS ? VAD_SILENCE_LONG_MS : VAD_SILENCE_MS)) {
              stopRecording();
              silenceStart = 0;
            }
          } else {
            silenceStart = 0;
          }
        }
      }

      rafId = setTimeout(vadLoop, 30);
    })();

    return () => {
      cancelled = true;
      setListening(false);
      setMicSpeaking(false);
      setVoiceInterim('');
      if (rafId) clearTimeout(rafId);
      if (micStream) micStream.getTracks().forEach(t => t.stop());
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close().catch(() => {});
      if (replyAudioRef.current) {
        try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
        replyAudioRef.current = null;
      }
    };
  }, [overlayMuted, micActive, hostOwnsMic, voiceTurns]);

  useEffect(() => {
    if (!injectQuestion || !onClearInjectQuestion) return;
    const injected = typeof injectQuestion === 'string' ? { text: injectQuestion } : injectQuestion;
    sendQuestion(injected.text, { voiceTurn: injected.voiceTurn, inputMode: injected.inputMode, diagnosticTest: injected.diagnosticTest === true, capability:injected.capability, requireScreen: injected.requireScreen === true }).catch((error) => {
      console.warn('[Assist] injected turn failed:', error?.message || error);
    });
    onClearInjectQuestion();
  }, [injectQuestion, onClearInjectQuestion, sendQuestion]);

  // ── HER REPLIES GO BACK TO THE CHAT PANEL ────────────────────────────
  // THE SCREENSHOT (Braeden, 8/25): the panel showed a column of his blue
  // bubbles and not one reply from her. In assist mode App.jsx hands every
  // turn to this engine through injectQuestion and returns immediately, so
  // the user bubble gets appended and nothing ever appends hers - the answer
  // lived here, in local state, and the window he was looking at never saw it.
  // It looked exactly like she was ignoring him. She was answering into a
  // component he could not see.
  //
  // ONE PLACE, NOT FOURTEEN. There are fourteen setReply() call sites in this
  // file. Wrapping each one is how a rule ends up enforced in thirteen lanes
  // and leaking through the fourteenth - the same mistake as the or-question
  // guard. Watching the state itself catches every path into it, including
  // ones added later by someone who never reads this comment.
  const lastRelayedReplyRef = useRef('');
  useEffect(() => {
    const t = String(reply || '').trim();
    if (!t || t === lastRelayedReplyRef.current) return;
    lastRelayedReplyRef.current = t;
    try { if (typeof onReply === 'function') onReply(t, {inputMode:replyModeRef.current}); } catch (_) { /* never break assist over a bubble */ }
  }, [reply, onReply]);

  // ── Assist Mode auto-entry from learned recipe ──
  // capabilityLearner stashes the user's goal in sessionStorage before
  // navigating to #screen so we can kick the assist flow off with it as the
  // initial prompt. Read once on mount, clear the key, then fire as soon as
  // the screen stream is live (sendQuestion needs a frame to analyze).
  const assistGoalRef = useRef(null);
  const assistGoalFiredRef = useRef(false);
  useEffect(() => {
    try {
      if (typeof sessionStorage === 'undefined') return;
      const goal = sessionStorage.getItem('aria_assist_goal');
      sessionStorage.removeItem('aria_assist_goal');
      const trimmed = typeof goal === 'string' ? goal.trim() : '';
      if (trimmed) {
        assistGoalRef.current = trimmed;
        // Surface immediately in the HUD so the user sees what Aria is
        // working on, even before the first capture lands.
        setActiveCommand(trimmed);
      }
    } catch (_) { /* sessionStorage can throw in some sandboxes */ }
  }, []);

  useEffect(() => {
    if (assistGoalFiredRef.current) return;
    const goal = assistGoalRef.current;
    if (!goal) return;
    if (!stream) return; // wait for screen capture to be ready
    assistGoalFiredRef.current = true;
    assistGoalRef.current = null;
    // Tiny delay so the video element has a chance to start producing frames
    // (waitForCaptureBase64 has its own 5s patience, but starting cold means
    // the first analyze call burns most of that budget).
    const t = setTimeout(() => {
      try { sendQuestion(goal); } catch (_) {}
    }, 300);
    return () => clearTimeout(t);
  }, [stream, sendQuestion]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.electronAPI?.sendOverlayListening) {
      window.electronAPI.sendOverlayListening(listening);
    }
  }, [listening]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.electronAPI?.sendOverlayAriaSpeaking) {
      window.electronAPI.sendOverlayAriaSpeaking(ariaSpeaking);
    }
  }, [ariaSpeaking]);

  // Audio ducking: smooth suppression during conversation (stays ducked until conversation ends + cooldown)
  const screenDuckedRef = useRef(false);
  const screenDuckTimerRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.duckSystemAudio) return;
    const active = ariaSpeaking || loading;
    if (active && !screenDuckedRef.current) {
      if (screenDuckTimerRef.current) { clearTimeout(screenDuckTimerRef.current); screenDuckTimerRef.current = null; }
      screenDuckedRef.current = true;
      window.electronAPI.duckSystemAudio().catch(() => {});
    } else if (!active && screenDuckedRef.current) {
      if (screenDuckTimerRef.current) clearTimeout(screenDuckTimerRef.current);
      screenDuckTimerRef.current = setTimeout(() => {
        screenDuckTimerRef.current = null;
        screenDuckedRef.current = false;
        window.electronAPI.restoreSystemAudio?.().catch(() => {});
      }, 3000);
    } else if (active && screenDuckedRef.current) {
      if (screenDuckTimerRef.current) { clearTimeout(screenDuckTimerRef.current); screenDuckTimerRef.current = null; }
    }
  }, [ariaSpeaking, loading]);
  useEffect(() => () => { if (screenDuckTimerRef.current) clearTimeout(screenDuckTimerRef.current); }, []);

  const toggleMic = () => {
    setSpeechError('');
    setMicActive((a) => !a);
  };

  const startShare = async () => {
    setError('');
    if (onRequestShare) await onRequestShare();
  };

  const stopShare = () => {
    voiceTurns.cancel('assist');
    assistAbortRef.current?.abort();
    // Silence Aria IMMEDIATELY — the user asked to leave, nothing may keep
    // talking after the click. Pause any playing TTS + kill its safety timer.
    if (replyAudioRef.current) {
      try { replyAudioRef.current.pause(); replyAudioRef.current.currentTime = 0; } catch (_) {}
      replyAudioRef.current = null;
    }
    if (ariaSpeakingTimeoutRef.current) {
      clearTimeout(ariaSpeakingTimeoutRef.current);
      ariaSpeakingTimeoutRef.current = null;
    }
    // Abort any pending chain follow-up so Aria doesn't fire after stop.
    chainAbortedRef.current = true;
    if (chainTimerRef.current) {
      clearTimeout(chainTimerRef.current);
      chainTimerRef.current = null;
    }
    chainGoalRef.current = '';
    chainStepRef.current = 0;
    // Exit Leave Mode + clear the on-screen overlay.
    if (leaveModeRef.current) {
      setLeaveMode(false);
      leaveModeRef.current = false;
    }
    try {
      if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendOnScreenAnnotations === 'function') {
        window.electronAPI.sendOnScreenAnnotations({ annotations: [], message: '' });
      }
    } catch (_) {}
    setLoading(false);
    setMicActive(false);
    setListening(false);
    setAriaSpeaking(false);
    setVoiceInterim('');
    setActiveCommand('');
    setSpeechError('');
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
    if (onStopShare) onStopShare();
    setReply('');
    setAnsweredByModel('');
    setAnnotations([]);
  };

  const MODEL_LABELS = {
    gemini: 'Gemini',
    opus: 'Claude Opus',
    grok: 'Grok',
    perplexity: 'Perplexity',
  };

  return (
    <div className={`screen-share-view${micOn ? ' screen-share-view--voice-only' : ''}`}>
      {/* ── THE RING, AND NOTHING ELSE (Braeden 9/5) ─────────────────────
          "i need to see this same thing when im IN assist mode ... thats the
           only thing i see, not the chat box, only that, if i mute that than
           the chat box pops up, but if i click on the mic in the chat box,
           than its only the circle IN ASSIST MODE"

          Mic on: the home-screen ring (TalkingBall - LISTENING / THINKING /
          SPEAKING, the whole circle is the mute button) fills the view and
          everything else is hidden. Mic off: the normal view with the chat
          box, whose mic button brings the ring back. The hidden view is kept
          MOUNTED, not unmounted: the preview <video> and overlay canvas are
          what the screenshots are drawn from, so they stay alive at 1px,
          opacity 0 (see .screen-share-view--voice-only in the CSS). */}
      {micOn && (
        <div className="assist-voice-only" role="region" aria-label="Aria is listening">
          <TalkingBall
            speaking={ariaSpeaking}
            thinking={!ariaSpeaking && loading}
            listening={!ariaSpeaking && !loading}
            muted={false}
            onToggleMute={toggleMic}
            size={300}
          />
        </div>
      )}
      <header className="screen-share-header">
        <button type="button" className="back-btn" onClick={() => { stopShare(); onBack(); }}>
          Back
        </button>
        <h2>Assist</h2>
          <p className="screen-share-tagline">
            {typeof window !== 'undefined' && window.electronAPI
              ? 'Let Aria see your screen to help you. She will capture your full display and listen for your voice commands.'
              : 'Let Aria see your screen to help you. When the dialog appears, choose "Entire Screen" or your display so she has full access.'}
          </p>
          {typeof window !== 'undefined' && window.electronAPI && window.electronAPI.platform === 'darwin' && (
            <p className="screen-share-hint screen-share-hint--permission">
              For full control (see screen + click/scroll), allow <strong>Screen Recording</strong> and <strong>Accessibility</strong> for Aria in System Settings.
              <button type="button" className="screen-share-open-settings" onClick={() => window.electronAPI?.openScreenRecordingSettings?.()}>Screen Recording</button>
              <button type="button" className="screen-share-open-settings" onClick={() => window.electronAPI?.openAccessibilitySettings?.()}>Accessibility</button>
            </p>
          )}
          {typeof window !== 'undefined' && window.electronAPI && window.electronAPI.platform === 'win32' && (
            <p className="screen-share-hint screen-share-hint--permission">
              On Windows, pick your display when prompted. For remote click/scroll, keep the desktop agent enabled and the backend reachable — same as on Mac.
              <button type="button" className="screen-share-open-settings" onClick={() => window.electronAPI?.openScreenRecordingSettings?.()}>Privacy settings</button>
              <button type="button" className="screen-share-open-settings" onClick={() => window.electronAPI?.openMicrophoneSettings?.()}>Microphone</button>
            </p>
          )}
      </header>

      {!stream && !isDesktopApp ? (
        <div className="screen-share-start screen-share-allow-step">
          {/* Browser: must show permission prompt since getDisplayMedia requires user gesture */}
            <div className="screen-share-allow-card" role="region" aria-labelledby="allow-screen-heading">
              <h3 id="allow-screen-heading" className="screen-share-allow-title">
                Assist needs to see your screen
              </h3>
              <p className="screen-share-allow-desc">
                When the dialog appears, choose your entire screen or a display so Aria can assist you with what's on it.
              </p>
              <button
                type="button"
                className="start-share-btn start-share-btn--allow"
                onClick={startShare}
                disabled={shareRequesting}
                aria-describedby="allow-screen-heading"
              >
                {shareRequesting ? 'Please wait…' : 'Allow'}
              </button>
              <p className="screen-share-allow-hint">In the dialog, select <strong>Entire Screen</strong> or your display (or your browser may pick the display automatically).</p>
            </div>
          {(screenShareError || error) && (
            <div className="screen-share-error">
              <span>{screenShareError || error}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="screen-share-active">
          {/* Desktop: show inline permission banner if screen capture isn't connected yet */}
          {!stream && isDesktopApp && (
            <div className="screen-share-connect-banner" role="status" aria-live="polite">
              {shareRequesting && !screenShareError && !error ? (
                <span>Connecting to your screen…</span>
              ) : (screenShareError || error) ? (
                <>
                  <span>{screenShareError || error}</span>
                  <button type="button" className="screen-share-open-settings" onClick={() => window.electronAPI?.openScreenRecordingSettings?.()}>
                    {window.electronAPI?.platform === 'win32' ? 'Open Settings' : 'Open System Settings'}
                  </button>
                  <button type="button" className="start-share-btn start-share-btn--inline" onClick={startShare} disabled={shareRequesting}>
                    Retry
                  </button>
                </>
              ) : (
                <>
                  <span>Screen capture not connected — Aria can still assist via accessibility.</span>
                  <button type="button" className="start-share-btn start-share-btn--inline" onClick={startShare} disabled={shareRequesting}>
                    Connect Screen
                  </button>
                </>
              )}
            </div>
          )}
          {stream && (
          <div className="screen-share-live-banner" role="status" aria-live="polite">
            <span className="screen-share-live-dot" aria-hidden />
            <span>Screen is being shared</span>
            <button type="button" className="screen-share-stop-in-banner" onClick={stopShare} title="Stop sharing your screen">
              Stop sharing
            </button>
          </div>
          )}
          <div
            className={`screen-share-command-hud screen-share-command-hud--${
              ariaSpeaking
                ? 'speaking'
                : loading
                  ? 'working'
                  : voiceInterim || (micOn && micSpeaking && !ariaSpeaking && !loading)
                    ? 'hearing'
                    : listening
                      ? 'hearing'
                      : 'ready'
            }`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="screen-share-command-hud-dot" aria-hidden />
            <div className="screen-share-command-hud-text">
              <span className="screen-share-command-hud-title">
                {ariaSpeaking
                  ? 'Aria is speaking'
                  : loading
                    ? 'Working on your screen'
                    : voiceInterim
                      ? 'Aria hears you'
                      : micOn && micSpeaking && !ariaSpeaking && !loading
                        ? 'Aria can hear you'
                        : listening
                          ? 'Listening'
                          : 'Assist'}
              </span>
              <span
                className="screen-share-command-hud-detail"
                title={
                  ariaSpeaking
                    ? truncAssist(reply || activeCommand, 500)
                    : loading
                      ? truncAssist(activeCommand, 500)
                      : voiceInterim
                        ? truncAssist(voiceInterim, 500)
                        : truncAssist(activeCommand, 500)
                }
              >
                {ariaSpeaking
                  ? truncAssist(reply || activeCommand, 120)
                  : loading
                    ? truncAssist(activeCommand, 120)
                    : voiceInterim
                      ? truncAssist(voiceInterim, 120)
                      : micOn && micSpeaking && !ariaSpeaking && !loading
                        ? "Your mic is on — we're picking up your voice."
                        : listening && !voiceInterim
                          ? "Speak now — we're listening for your words."
                          : activeCommand
                            ? `Last command: ${truncAssist(activeCommand, 100)}`
                            : 'Speak or type — your command shows here.'}
              </span>
            </div>
          </div>
          <div className="screen-share-body">
            {stream && (
            <div className="screen-preview-wrap">
              <video ref={videoRef} autoPlay muted playsInline className="screen-video" />
              {!previewReady && (
                <div className="screen-preview-loading" aria-live="polite">
                  Preview loading…
                </div>
              )}
              <canvas ref={canvasRef} className="screen-overlay" />
              {/* Agent Action Status Overlay — shows what AI is doing on the computer */}
              {agentStatus && (
                <div className={`agent-status-pill agent-status-pill--${agentStatus.phase || 'executing'}${agentStatus.fading ? ' agent-status-pill--fading' : ''}`} role="status" aria-live="polite">
                  <span className="agent-status-icon" aria-hidden>
                    {agentStatus.phase === 'executing' ? '⚡' :
                     agentStatus.phase === 'verifying' ? '🔍' :
                     agentStatus.phase === 'verified' ? '✓' :
                     agentStatus.phase === 'correcting' ? '🔄' :
                     agentStatus.phase === 'needsPermission' ? '🙋' :
                     agentStatus.phase === 'paused' ? '⏸' :
                     agentStatus.phase === 'error' ? '✗' :
                     agentStatus.phase === 'done' ? '✓' : '⚡'}
                  </span>
                  <span className="agent-status-text">{agentStatus.description || 'Working...'}</span>
                  {/* Permission-first: Aria asked to go ahead — answer by click (or just say "yes"/"wait") */}
                  {agentStatus.phase === 'needsPermission' && (
                    <span className="agent-status-consent" style={{ display: 'inline-flex', gap: 8, marginLeft: 10 }}>
                      <button
                        type="button"
                        onClick={() => { try { window.electronAPI?.assistConsent(true); } catch (_) {} setAgentStatus({ phase: 'executing', description: 'On it.' }); }}
                        style={{ font: 'inherit', fontSize: 13, fontWeight: 700, color: '#fff', background: 'linear-gradient(160deg,#00d9ff,#0a84ff)', border: 'none', borderRadius: 999, padding: '6px 14px', cursor: 'pointer' }}
                      >Go ahead</button>
                      <button
                        type="button"
                        onClick={() => { try { window.electronAPI?.assistConsent(false); } catch (_) {} setAgentStatus(null); }}
                        style={{ font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text-2,#aaa)', background: 'rgba(255,255,255,0.10)', border: 'none', borderRadius: 999, padding: '6px 14px', cursor: 'pointer' }}
                      >Wait</button>
                    </span>
                  )}
                  {agentStatus.total > 1 && agentStatus.step && agentStatus.phase !== 'needsPermission' && (
                    <span className="agent-status-progress">
                      <span className="agent-status-progress-bar" style={{ width: `${Math.round((agentStatus.step / agentStatus.total) * 100)}%` }} />
                    </span>
                  )}
                </div>
              )}
            </div>
            )}
            <div className="screen-mic-wrap">
              {listening && <span className="screen-mic-listening">Listening…</span>}
              {speechError && <span className="screen-share-error">{speechError}</span>}
            </div>
            {/* Debug bar removed — production */}
            <div className="screen-aria-response">
              <div className="screen-aria-response-label">
                {ariaSpeaking ? (
                  <span className="screen-aria-speaking">
                    <span className="screen-aria-speaking-dot" aria-hidden />
                    Aria is speaking…
                  </span>
                ) : (
                  'Aria'
                )}
              </div>
              <div className="screen-aria-response-box">
                {reply ? (
                  <>
                    <p className="screen-aria-response-text">{reply}</p>
                    {answeredByModel && MODEL_LABELS[answeredByModel] && (
                      <span className="screen-aria-model-badge" title={`Answered by ${MODEL_LABELS[answeredByModel]}`}>
                        {MODEL_LABELS[answeredByModel]}
                      </span>
                    )}
                  </>
                ) : (
                  <p className="screen-aria-response-placeholder">
                    {loading ? 'Thinking\u2026' : "Ask something to hear Aria's reply here."}
                  </p>
                )}
              </div>
              {/* Action Cards — rich visual results for food, shopping, rides, etc. */}
              {actionCards && actionCards.length > 0 && (
                <div className="aria-action-cards">
                  {actionCards.map((card) => (
                    <div key={card.id} className={`aria-action-card aria-action-card--${card.category || 'default'}`}>
                      <div className="aria-action-card-img-wrap">
                        {card.image ? (
                          <img
                            src={card.image}
                            alt={card.title}
                            className="aria-action-card-img"
                            loading="lazy"
                            onError={(e) => {
                              // Replace broken image with title initial
                              e.target.style.display = 'none';
                              const wrap = e.target.parentElement;
                              if (wrap && !wrap.querySelector('.aria-action-card-img-fallback')) {
                                const fb = document.createElement('span');
                                fb.className = 'aria-action-card-img-fallback';
                                fb.textContent = (card.title || '?')[0].toUpperCase();
                                wrap.appendChild(fb);
                              }
                            }}
                          />
                        ) : (
                          <span className="aria-action-card-img-fallback">{(card.title || '?')[0].toUpperCase()}</span>
                        )}
                      </div>
                      <div className="aria-action-card-body">
                        <span className="aria-action-card-title">{card.title}</span>
                        {card.subtitle && <span className="aria-action-card-subtitle">{card.subtitle}</span>}
                        {card.price && <span className="aria-action-card-price">{card.price}</span>}
                      </div>
                      <button
                        type="button"
                        className="aria-action-card-btn"
                        onClick={() => {
                          if (card.actionUrl && window.electronAPI?.openExternal) {
                            window.electronAPI.openExternal(card.actionUrl);
                          } else if (card.actionUrl) {
                            window.open(card.actionUrl, '_blank', 'noopener');
                          }
                        }}
                      >
                        {card.actionLabel || 'Open'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Centered mic orb — matches home screen design */}
          <div className="assist-mic-orb-row">
            <button
              type="button"
              className={[
                'assist-mic-orb',
                micOn && 'assist-mic-orb--on',
                ariaSpeaking && 'assist-mic-orb--aria-speaking',
                micOn && micSpeaking && !ariaSpeaking && 'assist-mic-orb--user-speaking',
                micOn && !ariaSpeaking && !micSpeaking && 'assist-mic-orb--idle',
                loading && 'assist-mic-orb--working',
              ].filter(Boolean).join(' ')}
              onClick={toggleMic}
              aria-label={micOn ? 'Mic on — tap to mute' : 'Turn on mic'}
              title={micOn ? 'Mic on — tap to mute' : 'Turn on mic to talk to Aria'}
            >
              <span className={`assist-mic-orb-inner${micOn ? ' assist-mic-orb-inner--orb' : ''}`}>
                {micOn ? (
                  // VOICE MODE — the real orb, the one from the home screen.
                  // It reads the same two states everything else does: she is
                  // speaking, or she is listening to you.
                  <AriaOrb
                    speaking={ariaSpeaking}
                    listening={!ariaSpeaking && (micSpeaking || listening)}
                    fill
                  />
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                )}
              </span>
            </button>
            <span className="assist-mic-orb-label" aria-live="polite">
              {ariaSpeaking ? 'ARIA' : micOn && micSpeaking ? 'YOU' : micOn ? (listening ? 'LISTENING' : 'READY') : 'MUTED'}
            </span>
          </div>
          {/* ── ONE OR THE OTHER (Braeden 9/5) ─────────────────────────────
              "it needs to be like a one or other with text box or the aria
               talking circle so if they click on the mic it shows the circle"

              Before this both were on screen at once: a circle to talk to AND
              a text box to type in, with nothing saying which one she was
              actually listening to. Now the mic button IS the switch. Mic on
              and you get the orb and nothing else; mic off and you get the
              box. Tapping the orb mutes and brings the box back, so the way
              out is the same control as the way in. */}
          {!micOn && (
          <div className="screen-share-input-row">
            <div className="input-add-wrap" ref={addMenuRef}>
              <button
                type="button"
                className="input-add-btn"
                aria-label="Add"
                title="Add"
                onClick={(e) => { e.stopPropagation(); setAddMenuOpen((o) => !o); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              {addMenuOpen && (
                <div className="add-menu">
                  <button type="button" className="add-menu-item add-menu-item--disabled" disabled>
                    Assist (active)
                  </button>
                  <button type="button" className="add-menu-item" onClick={() => { setAddMenuOpen(false); onPickFiles?.(); }}>
                    Files
                  </button>
                  <button type="button" className="add-menu-item" onClick={() => { setAddMenuOpen(false); onTakePicture?.(); }}>
                    Take a picture
                  </button>
                </div>
              )}
            </div>
            <span role="status">Tap the microphone to speak to Aria.</span>
          </div>
          )}
          {/* STOP SHARING LIVES OUTSIDE THE SWITCH. It used to sit inside the
              text row, which would have meant that turning the mic on took
              away the only way to stop sharing your screen - a worse bug than
              the one being fixed. */}
          {stream && (
            <div className="screen-share-stop-row">
              <button type="button" className="screen-share-stop-btn" onClick={stopShare} title="Stop sharing your screen">
                Stop sharing
              </button>
            </div>
          )}
          {/* ── Aria Settings Panel ── */}
          <div className="aria-settings-panel">
            <button
              type="button"
              className="aria-settings-toggle"
              onClick={() => setSettingsOpen((o) => !o)}
              aria-expanded={settingsOpen}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
              <span>Settings</span>
              <svg className={`aria-settings-chevron ${settingsOpen ? 'aria-settings-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {settingsOpen && (
              <div className="aria-settings-body">
                {/* Computer Control */}
                <div className="aria-settings-section">
                  <h4 className="aria-settings-section-title">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                    Computer Control
                  </h4>
                  <p className="aria-settings-desc">Let Aria click, scroll, and interact with your screen.</p>
                  {isDesktopApp ? (
                    <>
                      <label className="aria-settings-toggle-label">
                        <span className={`aria-settings-switch ${desktopAgentEnabled ? 'aria-settings-switch--on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={desktopAgentEnabled}
                            onChange={() => {
                              const next = !desktopAgentEnabled;
                              setDesktopAgentEnabled(next);
                              window.electronAPI?.setAgentConfig?.({ agentEnabled: next });
                            }}
                          />
                          <span className="aria-settings-switch-track" />
                        </span>
                        <span>{desktopAgentEnabled ? 'Aria can control your computer' : 'Computer control is off'}</span>
                      </label>
                      {desktopAgentEnabled && (
                        <label className="aria-settings-toggle-label aria-settings-sub-option">
                          <span className={`aria-settings-switch ${desktopRunWhenIdleOnly ? 'aria-settings-switch--on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={desktopRunWhenIdleOnly}
                              onChange={() => {
                                const next = !desktopRunWhenIdleOnly;
                                setDesktopRunWhenIdleOnly(next);
                                window.electronAPI?.setAgentConfig?.({ agentRunWhenIdleOnly: next });
                              }}
                            />
                            <span className="aria-settings-switch-track" />
                          </span>
                          <span>Only when I&apos;m idle</span>
                        </label>
                      )}
                      {agentDeviceId && (
                        <div className="aria-settings-device-id">
                          <span className="aria-settings-device-id-label">Device ID</span>
                          <code className="aria-settings-device-id-value">{agentDeviceId}</code>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="aria-settings-desc">On the web, remote control requires the <strong>Aria Agent</strong> desktop app. Install it, then paste the device ID below.</p>
                      <input
                        type="text"
                        className="aria-settings-input"
                        placeholder="Paste device ID from Aria Agent"
                        value={agentDeviceId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAgentDeviceId(v);
                          try { localStorage.setItem('shimmer_agent_device_id', v); } catch (_) {}
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {error && <p className="screen-share-error screen-share-error--spoken">{error}</p>}
        </div>
      )}
    </div>
  );
}

export default React.memo(ScreenShareView);
